import { React, useEffect, useState } from 'react'
import { Container, Stack, Table, Alert, Button, Modal, Form, Row, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Login from '../Components/Admin/Login';
import { onAuthStateChanged } from 'firebase/auth';
import Navigation from '../Components/Admin/Navigation';
import { auth, db } from '../utils/firebase'
import { collection, getDocs, addDoc, serverTimestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

export default function UkiesAdminSelections(props) {
    const [ token, setToken ] = useState(localStorage.getItem('ukies-webinars-accessToken'))
    const [ user, setUser ] = useState(JSON.parse(localStorage.getItem('ukies-webinars-admin')))
    const [ selections, setSelections ] = useState([])
    const [ loading, setLoading ] = useState(false)
    const [ showAlert, setShowAlert ] = useState(false)
    const [ alertMessage, setAlertMessage ] = useState('')
    const [ alertType, setAlertType ] = useState('success')
    const [ authReady, setAuthReady ] = useState(false)
    const [ showModal, setShowModal ] = useState(false)
    const [ formData, setFormData ] = useState({})
    const [ saving, setSaving ] = useState(false)
    const [ forms, setForms ] = useState([])
    const [ formFields, setFormFields ] = useState([])
    const [ formFieldsLoading, setFormFieldsLoading ] = useState(false)
    const [ categories, setCategories ] = useState([])
    const [ products, setProducts ] = useState([])
    const [ csvGenerating, setCsvGenerating ] = useState(false)
    const [ csvProgress, setCsvProgress ] = useState('')
    const [ showViewModal, setShowViewModal ] = useState(false)
    const [ selectedSelection, setSelectedSelection ] = useState(null)
    const [ viewFormData, setViewFormData ] = useState({})
    const [ deleteEnabled, setDeleteEnabled ] = useState(false)
    const [ deleting, setDeleting ] = useState(false)
    const [ downloading, setDownloading ] = useState(false)
    const [ showFormsDropdown, setShowFormsDropdown ] = useState(false)
    const [ showFieldsDropdown, setShowFieldsDropdown ] = useState(false)
    const [ showProductsDropdown, setShowProductsDropdown ] = useState(false)
    const [ productSearchTerm, setProductSearchTerm ] = useState('')

    // Handle click outside to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Close forms dropdown if clicking outside
            if (showFormsDropdown && !event.target.closest('#forms-dropdown')?.parentElement?.parentElement) {
                setShowFormsDropdown(false);
            }
            
            // Close fields dropdown if clicking outside
            if (showFieldsDropdown && !event.target.closest('#fields-dropdown')?.parentElement?.parentElement) {
                setShowFieldsDropdown(false);
            }
            
            // Close products dropdown if clicking outside
            if (showProductsDropdown && !event.target.closest('#products-dropdown')?.parentElement?.parentElement) {
                setShowProductsDropdown(false);
            }
        };

        // Add event listener
        document.addEventListener('click', handleClickOutside);
        
        // Cleanup
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showFormsDropdown, showFieldsDropdown, showProductsDropdown]);

    // Token for API calls
    const apiToken = process.env.REACT_APP_UKIES_API_TOKEN;

    // Translate selection types
    const translateSelectionType = (type) => {
        switch(type) {
            case 'newsletter':
                return 'Nieuwsbrief inschrijvingen';
            case 'specific_products':
                return 'Product aankopen';
            case 'product_category':
                return 'Categorie aankopen';
            case 'form_entries':
                return 'Formulier inzendingen';
            default:
                return type;
        }
    };

    // Turn a form field name (e.g. "firstName") into a readable label when no label is set
    const prettifyFieldName = (name) => {
        if (!name) return '';
        const spaced = name
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .replace(/[_-]+/g, ' ')
            .trim();
        return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    };

    // Format selection parameters for display
    const formatSelectionParameters = (parameters) => {
        if (!parameters) return '';
        
        const { categories: categoryIds, forms: formIds, products: productIds, operator } = parameters;
        
        // Helper function to add operator prefix if multiple items
        const addOperatorPrefix = (itemsArray, itemsList) => {
            if (itemsList.length > 1) {
                const operatorText = operator === 'and' ? 'Alle onderstaande' : 'Minstens één van de onderstaande';
                return (
                    <>
                        <strong>{operatorText}</strong>
                        <br />
                        {itemsArray.map((item, index) => (
                            <span key={index}>
                                {item}
                                {index < itemsArray.length - 1 && <br />}
                            </span>
                        ))}
                    </>
                );
            }
            return itemsArray[0]; // Single item, no prefix needed
        };
        
        if (categoryIds && categoryIds.length > 0) {
            // Sort category IDs by number (ascending) and look up names
            const sortedCategoryIds = [...categoryIds].sort((a, b) => parseInt(a) - parseInt(b));
            const categoryNames = sortedCategoryIds.map(id => {
                const category = categories.find(cat => cat.id.toString() === id.toString());
                const categoryName = category ? category.name || category.title || `Category ${id}` : `Unknown Category (${id})`;
                return `${id}) ${categoryName}`;
            });
            return addOperatorPrefix(categoryNames, categoryNames);
        }
        
        if (formIds && formIds.length > 0) {
            // Sort form IDs by number (ascending) and look up names
            const sortedFormIds = [...formIds].sort((a, b) => {
                // Handle 'newsletter' as a special case
                if (a === 'newsletter') return -1;
                if (b === 'newsletter') return 1;
                return parseInt(a) - parseInt(b);
            });
            const formNames = sortedFormIds.map(id => {
                if (id === 'newsletter') return 'newsletter) Nieuwsbrief';
                const form = forms.find(f => f.id.toString() === id.toString());
                const formName = form ? form.name || form.title || `Form ${id}` : `Unknown Form (${id})`;
                return `${id}) ${formName}`;
            });
            return addOperatorPrefix(formNames, formNames);
        }
        
        if (productIds && productIds.length > 0) {
            // Sort product IDs by number (ascending) and look up names
            const sortedProductIds = [...productIds].sort((a, b) => parseInt(a) - parseInt(b));
            const productNames = sortedProductIds.map(id => {
                const product = products.find(p => p.id.toString() === id.toString());
                const productName = product ? product.title || `Product ${id}` : `Unknown Product (${id})`;
                return `${id}) ${productName}`;
            });
            return addOperatorPrefix(productNames, productNames);
        }
        
        return '';
    };

    // Format number with Dutch thousand notation
    const formatNumber = (number) => {
        if (!number && number !== 0) return '';
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };

    // Format date for display
    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        
        try {
            // Handle Firestore Timestamp
            const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
            return date.toLocaleString('nl-NL', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return '';
        }
    };

    // CSV Generation Functions

    // Step 1: Fetch form data from Eliteswitch API
    const fetchFormData = async (formIds) => {
        let allFormEntries = [];
        
        for (const formId of formIds) {
            setCsvProgress(`Formuliergegevens ophalen voor formulier ${formId}...`);
            
            try {
                const response = await fetch('https://ukies-api-search.eliteswitch.com/indexes/webshops_forms_entries/search', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${apiToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        "q": null,
                        "sort": ["id:desc"],
                        "matchingStrategy": "all",
                        "facets": ["*"],
                        "filter": [[`form_id='${formId}'`]],
                        "limit": 90000
                    })
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                
                // Check API limits
                if (data.estimatedTotalHits > data.limit) {
                    throw new Error(`Er is een fout opgetreden. De formulieren API is ingesteld op een limiet van ${data.limit}, maar er waren ${data.estimatedTotalHits} resultaten. Jaap moet dit aanpassen.`);
                }

                // Get form name from forms state
                const formName = forms.find(f => f.id.toString() === formId.toString())?.name || `Form ${formId}`;
                
                // Process form entries
                const processedEntries = data.hits.map(entry => {
                    // Format optin date as YYYY-MM-DD HH24:Mi:SS
                    let formattedOptinDate = '';
                    if (entry.created_at) {
                        try {
                            const date = new Date(entry.created_at);
                            formattedOptinDate = date.getFullYear() + '-' + 
                                (date.getMonth() + 1).toString().padStart(2, '0') + '-' + 
                                date.getDate().toString().padStart(2, '0') + ' ' + 
                                date.getHours().toString().padStart(2, '0') + ':' + 
                                date.getMinutes().toString().padStart(2, '0') + ':' + 
                                date.getSeconds().toString().padStart(2, '0');
                        } catch (error) {
                            formattedOptinDate = entry.created_at;
                        }
                    }
                    
                    return {
                        email: entry.payload?.email || '',
                        optin: 'Y',
                        optinDate: formattedOptinDate,
                        optinTimestamp: entry.created_at_timestamp || 0,
                        optinForm: formName,
                        optinSource: entry.payload?.locatie ? `${formName} | ${entry.payload.locatie}` : formName
                    };
                });

                allFormEntries = [...allFormEntries, ...processedEntries];
                
            } catch (error) {
                console.error('Error fetching form data:', error);
                throw error;
            }
        }
        
        return allFormEntries;
    };

    // Fetch raw entries for a single form (used by the form_entries selection type), keyed by the form's own field names
    const fetchFormEntriesRaw = async (formId) => {
        setCsvProgress('Formuliergegevens ophalen...');

        try {
            const response = await fetch('https://ukies-api-search.eliteswitch.com/indexes/webshops_forms_entries/search', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    "q": null,
                    "sort": ["id:desc"],
                    "matchingStrategy": "all",
                    "facets": ["*"],
                    "filter": [[`form_id='${formId}'`]],
                    "limit": 90000
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // Check API limits
            if (data.estimatedTotalHits > data.limit) {
                throw new Error(`Er is een fout opgetreden. De formulieren API is ingesteld op een limiet van ${data.limit}, maar er waren ${data.estimatedTotalHits} resultaten. Jaap moet dit aanpassen.`);
            }

            return data.hits.map(entry => {
                const payload = typeof entry.payload === 'string' ? JSON.parse(entry.payload) : (entry.payload || {});
                return { ...payload };
            });

        } catch (error) {
            console.error('Error fetching form entries:', error);
            throw error;
        }
    };

    // Step 2: Deduplicate data based on conditions
    const deduplicateData = (entries, onlyLastOptin, fieldsEnabled) => {
        if (!onlyLastOptin) {
            return entries; // No deduplication
        }

        setCsvProgress('Duplicaten verwijderen...');
        
        const optinSourceSelected = fieldsEnabled.includes('optinSource');
        
        if (optinSourceSelected) {
            // Deduplicate on email + optinForm, keep highest optinTimestamp
            const grouped = entries.reduce((acc, entry) => {
                const key = `${entry.email}|${entry.optinForm}`;
                if (!acc[key] || entry.optinTimestamp > acc[key].optinTimestamp) {
                    acc[key] = entry;
                }
                return acc;
            }, {});
            return Object.values(grouped);
        } else {
            // Deduplicate on email only, keep highest optinTimestamp
            const grouped = entries.reduce((acc, entry) => {
                if (!acc[entry.email] || entry.optinTimestamp > acc[entry.email].optinTimestamp) {
                    acc[entry.email] = entry;
                }
                return acc;
            }, {});
            return Object.values(grouped);
        }
    };

    // Step 3: Fetch customer data and merge
    const fetchAndMergeCustomerData = async (entries, fieldsEnabled) => {
        const customerFields = ['id', 'firstname', 'lastname', 'phone', 'city', 'zipcode', 'country'];
        const needsCustomerData = fieldsEnabled.some(field => customerFields.includes(field));
        
        if (!needsCustomerData) {
            return entries; // No customer data needed
        }

        setCsvProgress('Klantgegevens ophalen...');
        
        try {
            const response = await fetch('https://ukies-api-search.eliteswitch.com/indexes/customers/search', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    "q": "",
                    "sort": ["id:desc"],
                    "matchingStrategy": "all",
                    "facets": ["*"],
                    "limit": 90000
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            // Check API limits
            if (data.estimatedTotalHits > data.limit) {
                throw new Error(`Er is een fout opgetreden. De klanten API is ingesteld op een limiet van ${data.limit}, maar er waren ${data.estimatedTotalHits} resultaten. Jaap moet dit aanpassen.`);
            }

            setCsvProgress('Klantgegevens koppelen...');

            // Create email to customer mapping
            const customerMap = {};
            data.hits.forEach(customer => {
                if (customer.email) {
                    customerMap[customer.email.toLowerCase()] = customer;
                }
            });

            // Merge customer data with entries
            return entries.map(entry => {
                const customer = customerMap[entry.email.toLowerCase()];
                if (customer && fieldsEnabled.includes('id')) entry.id = customer.id || '';
                if (customer && fieldsEnabled.includes('firstname')) entry.firstname = customer.firstname || '';
                if (customer && fieldsEnabled.includes('lastname')) entry.lastname = customer.lastname || '';
                if (customer && fieldsEnabled.includes('phone')) entry.phone = customer.phone || '';
                if (customer && fieldsEnabled.includes('city')) entry.city = customer.city || '';
                if (customer && fieldsEnabled.includes('zipcode')) entry.zipcode = customer.zipcode || '';
                if (customer && fieldsEnabled.includes('country')) entry.country = customer.country || '';
                
                // Track if this entry was matched to a customer
                entry._hasCustomerMatch = !!customer;
                
                return entry;
            });
            
        } catch (error) {
            console.error('Error fetching customer data:', error);
            throw error;
        }
    };

    // Step 4: Remove non-customers if needed
    const filterCustomersOnly = (entries, customersOnly) => {
        if (!customersOnly) {
            return entries; // Keep all entries
        }

        setCsvProgress('Alleen klanten behouden...');
        
        return entries.filter(entry => entry._hasCustomerMatch);
    };

    // Utility function to trigger automatic file download
    const triggerFileDownload = (url, filename) => {
        try {
            // Create a temporary anchor element to trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error triggering file download:', error);
            // Fallback: open in new tab
            window.open(url, '_blank');
        }
    };

    // Step 5: Generate CSV and upload to Firebase Storage
    // customLabels, when given (e.g. for form_entries), maps a field id directly to its CSV header label,
    // and fieldsEnabled's own order is used instead of the fixed dropdown order below
    const generateAndUploadCSV = async (entries, fieldsEnabled, selectionName, customLabels = null) => {
        setCsvProgress('CSV bestand genereren...');

        // Define field order and labels for CSV headers (same as dropdown order)
        const fieldOptions = [
            { id: 'id', label: 'Klantnummer' },
            { id: 'firstname', label: 'Voornaam' },
            { id: 'lastname', label: 'Achternaam' },
            { id: 'email', label: 'E-mail' },
            { id: 'phone', label: 'Telefoonnummer' },
            { id: 'city', label: 'Woonplaats' },
            { id: 'zipcode', label: 'Postcode' },
            { id: 'country', label: 'Land' },
            { id: 'purchasedProduct', label: 'Gekochte product' },
            { id: 'purchaseDate', label: 'Aankoop datum' },
            { id: 'optin', label: 'Optin status' },
            { id: 'optinDate', label: 'Optin datum' },
            { id: 'optinSource', label: 'Optin bron' }
        ];

        // Filter and order fields according to dropdown order
        const orderedFields = customLabels
            ? fieldsEnabled
            : fieldOptions
                .filter(field => fieldsEnabled.includes(field.id))
                .map(field => field.id);

        // Create CSV headers using field labels in correct order
        const headers = orderedFields.map(fieldId => {
            const headerLabel = customLabels
                ? (customLabels[fieldId] || fieldId)
                : (fieldOptions.find(f => f.id === fieldId)?.label || fieldId);
            // Always wrap headers in double quotes and escape existing quotes
            return `"${headerLabel.toString().replace(/"/g, '""')}"`;
        });
        
        // Create CSV rows
        const csvRows = [headers.join(',')];
        
        entries.forEach(entry => {
            const row = orderedFields.map(field => {
                const value = entry[field] || '';
                // Always wrap values in double quotes and escape existing quotes
                return `"${value.toString().replace(/"/g, '""')}"`;
            });
            csvRows.push(row.join(','));
        });

        const csvContent = csvRows.join('\n');
        
        // Sanitize selection name for filename (replace spaces with underscores, remove special chars except underscores)
        const sanitizedName = selectionName
            .replace(/\s+/g, '_')  // Replace spaces with underscores
            .replace(/[^\w_]/g, ''); // Remove all special characters except underscores and word characters
        
        // Create filename with date and time prefix (YYYYMMDD_HH24MiSS_)
        const now = new Date();
        const dateTimePrefix = now.getFullYear().toString() + 
            (now.getMonth() + 1).toString().padStart(2, '0') + 
            now.getDate().toString().padStart(2, '0') + '_' +
            now.getHours().toString().padStart(2, '0') + 
            now.getMinutes().toString().padStart(2, '0') + 
            now.getSeconds().toString().padStart(2, '0') + '_';
        const filename = `${dateTimePrefix}${sanitizedName}.csv`;
        
        setCsvProgress('CSV bestand uploaden...');
        
        // Upload to Firebase Storage
        try {
            const storage = getStorage();
            const storageRef = ref(storage, `selections/${filename}`);
            
            // Convert CSV string to blob
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            
            // Upload file
            await uploadBytes(storageRef, blob);
            
            // Get download URL
            const downloadURL = await getDownloadURL(storageRef);
            
            return {
                fileLink: downloadURL,
                recordCount: entries.length,
                filename: filename
            };
            
        } catch (error) {
            console.error('Error uploading CSV:', error);
            throw new Error('Fout bij het uploaden van het CSV bestand.');
        }
    };

    // Specific Products CSV Generation Functions
    
    // Step 1: Fetch sales lines data from Eliteswitch API
    const fetchSalesLinesData = async (productIds, purchasedAfter, purchasedBefore, onlyLastPurchased, productOperator) => {
        setCsvProgress(`Verkoop gegevens ophalen voor producten...`);
        
        try {
            // Prepare filter
            const filters = [
                [`product_id IN [${productIds.join(',')}]`],
                [`sale_status='completed'`]
            ];

            const response = await fetch('https://ukies-api-search.eliteswitch.com/indexes/sales_lines/search', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    "q": null,
                    "sort": ["id:desc"],
                    "matchingStrategy": "all",
                    "facets": ["*"],
                    "filter": filters,
                    "limit": 90000
                })
            });

            if (!response.ok) {
                throw new Error(`API fout: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            let salesLines = data.hits || [];
            
            // Handle pagination if needed
            if (data.estimatedTotalHits > salesLines.length) {
                const totalHits = data.estimatedTotalHits;
                let offset = salesLines.length;
                
                while (offset < totalHits) {
                    setCsvProgress(`Verkoop gegevens ophalen... ${offset}/${totalHits}`);
                    
                    const nextResponse = await fetch('https://ukies-api-search.eliteswitch.com/indexes/sales_lines/search', {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${apiToken}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            "q": null,
                            "sort": ["id:desc"],
                            "matchingStrategy": "all",
                            "facets": ["*"],
                            "filter": filters,
                            "limit": 90000,
                            "offset": offset
                        })
                    });

                    if (!nextResponse.ok) {
                        console.error('Failed to fetch additional sales lines:', nextResponse.statusText);
                        break;
                    }

                    const nextData = await nextResponse.json();
                    const nextSalesLines = nextData.hits || [];
                    
                    if (nextSalesLines.length === 0) {
                        break;
                    }
                    
                    salesLines = salesLines.concat(nextSalesLines);
                    offset += nextSalesLines.length;
                }
            }

            // Step 1.1 & 1.2: Apply date filters
            if (purchasedAfter || purchasedBefore) {
                setCsvProgress('Datum filters toepassen...');
                salesLines = salesLines.filter(line => {
                    if (!line.created_at) return true;
                    
                    const createdDate = new Date(line.created_at).toISOString().split('T')[0]; // Extract date part only
                    
                    if (purchasedAfter && createdDate < purchasedAfter) {
                        return false;
                    }
                    
                    if (purchasedBefore && createdDate > purchasedBefore) {
                        return false;
                    }
                    
                    return true;
                });
            }

            // Step 1.3: Filter customers who have purchased ALL products if productOperator is "all"
            if (productOperator === 'all') {
                setCsvProgress('Alleen klanten behouden die alle geselecteerde producten hebben gekocht...');
                
                // Normalize productIds to ensure consistent type comparison
                const normalizedProductIds = productIds.map(id => parseInt(id));
                
                // Group sales lines by customer_id and track which products they bought
                const customerProducts = {};
                salesLines.forEach(line => {
                    const customerId = line.customer_id;
                    if (!customerProducts[customerId]) {
                        customerProducts[customerId] = new Set();
                    }
                    // Normalize product_id to integer for consistent comparison
                    customerProducts[customerId].add(parseInt(line.product_id));
                });
                
                // Find customers who have purchased all required products
                const validCustomers = new Set();
                Object.keys(customerProducts).forEach(customerId => {
                    const customerProductSet = customerProducts[customerId];
                    const hasAllProducts = normalizedProductIds.every(productId => customerProductSet.has(productId));
                    if (hasAllProducts) {
                        validCustomers.add(parseInt(customerId));
                    }
                });
                
                // Filter sales lines to keep only valid customers
                salesLines = salesLines.filter(line => validCustomers.has(line.customer_id));
                
                setCsvProgress(`${validCustomers.size} klanten hebben alle geselecteerde producten gekocht.`);
            }

            // Step 1.4: Deduplicate if "Alleen het laatst gekochte product tonen" is checked
            if (onlyLastPurchased) {
                setCsvProgress('Duplicaten verwijderen op basis van laatste aankoop...');
                const grouped = salesLines.reduce((acc, line) => {
                    const customerId = line.customer_id;
                    if (!acc[customerId] || new Date(line.created_at) > new Date(acc[customerId].created_at)) {
                        acc[customerId] = line;
                    }
                    return acc;
                }, {});
                salesLines = Object.values(grouped);
            }

            setCsvProgress(`${salesLines.length} verkoop records gevonden.`);
            return salesLines;
            
        } catch (error) {
            console.error('Error fetching sales lines:', error);
            throw error;
        }
    };

    // Step 2: Fetch all customers data from Eliteswitch API
    const fetchAllCustomersData = async () => {
        setCsvProgress('Alle klantgegevens ophalen...');
        
        try {
            const response = await fetch('https://ukies-api-search.eliteswitch.com/indexes/customers/search', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    "q": "",
                    "sort": ["id:desc"],
                    "matchingStrategy": "all",
                    "facets": ["*"],
                    "limit": 90000
                })
            });

            if (!response.ok) {
                throw new Error(`API fout: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            let customers = data.hits || [];
            
            // Handle pagination if needed
            if (data.estimatedTotalHits > customers.length) {
                const totalHits = data.estimatedTotalHits;
                let offset = customers.length;
                
                while (offset < totalHits) {
                    setCsvProgress(`Klantgegevens ophalen... ${offset}/${totalHits}`);
                    
                    const nextResponse = await fetch('https://ukies-api-search.eliteswitch.com/indexes/customers/search', {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${apiToken}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            "q": "",
                            "sort": ["id:desc"],
                            "matchingStrategy": "all",
                            "facets": ["*"],
                            "limit": 90000,
                            "offset": offset
                        })
                    });

                    if (!nextResponse.ok) {
                        console.error('Failed to fetch additional customers:', nextResponse.statusText);
                        break;
                    }

                    const nextData = await nextResponse.json();
                    const nextCustomers = nextData.hits || [];
                    
                    if (nextCustomers.length === 0) {
                        break;
                    }
                    
                    customers = customers.concat(nextCustomers);
                    offset += nextCustomers.length;
                }
            }

            setCsvProgress(`${customers.length} klant records gevonden.`);
            return customers;
            
        } catch (error) {
            console.error('Error fetching customers:', error);
            throw error;
        }
    };

    // Step 3: Merge sales lines with customer data
    const mergeSalesWithCustomers = async (salesLines, fieldsEnabled) => {
        setCsvProgress('Verkoop- en klantgegevens samenvoegen...');
        
        // Fetch all customers
        const customers = await fetchAllCustomersData();
        
        // Create customer lookup map
        const customerMap = {};
        customers.forEach(customer => {
            customerMap[customer.id] = customer;
        });

        // Merge sales lines with customer data
        const mergedData = salesLines
            .filter(line => customerMap[line.customer_id]) // Only keep sales lines with valid customers
            .map(line => {
                const customer = customerMap[line.customer_id];
                
                // Format purchasedProduct field
                const purchasedProduct = `(${line.product_id}) ${line.name || ''}`;
                
                // Format purchaseDate field
                const purchaseDate = line.created_at ? 
                    new Date(line.created_at).toISOString().replace('T', ' ').substring(0, 19) : '';

                return {
                    // Customer fields
                    id: customer.id,
                    firstname: customer.firstname || '',
                    lastname: customer.lastname || '',
                    email: customer.email || '',
                    phone: customer.phone || '',
                    city: customer.city || '',
                    zipcode: customer.zipcode || '',
                    country: customer.country || '',
                    // Product fields
                    purchasedProduct: purchasedProduct,
                    purchaseDate: purchaseDate,
                    // Sales line data for potential optin processing
                    _salesLine: line,
                    _customer: customer
                };
            });

        setCsvProgress(`${mergedData.length} records samengevoegd.`);
        return mergedData;
    };

    // Step 4: Fetch newsletter subscriptions and merge optin data
    const fetchAndMergeOptinData = async (entries, fieldsEnabled, newsletterRequired, optinFieldsSelected) => {
        // Check if we need to process newsletter data
        if (!newsletterRequired && !optinFieldsSelected) {
            // Set default optin values for all entries
            return entries.map(entry => ({
                ...entry,
                optin: 'N',
                optinDate: '',
                optinTimestamp: '',
                optinForm: '',
                optinSource: ''
            }));
        }

        setCsvProgress('Nieuwsbrief inschrijvingen ophalen...');
        
        try {
            // Get all newsletter forms
            const newsletterForms = forms.filter(form => 
                form.name && form.name.startsWith('Nieuwsbrief inschrijvingen - ')
            );
            
            let allFormEntries = [];
            
            // Fetch form entries for each newsletter form
            for (const form of newsletterForms) {
                setCsvProgress(`Nieuwsbrief gegevens ophalen voor formulier ${form.id}...`);
                
                const response = await fetch('https://ukies-api-search.eliteswitch.com/indexes/webshops_forms_entries/search', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${apiToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        "q": null,
                        "sort": ["id:desc"],
                        "matchingStrategy": "all",
                        "facets": ["*"],
                        "filter": [[`form_id='${form.id}'`]],
                        "limit": 90000
                    })
                });

                if (response.ok) {
                    const data = await response.json();
                    const formEntries = data.hits || [];
                    let currentFormEntries = [...formEntries];
                    
                    // Handle pagination if needed
                    if (data.estimatedTotalHits > formEntries.length) {
                        const totalHits = data.estimatedTotalHits;
                        let offset = formEntries.length;
                        
                        while (offset < totalHits) {
                            const nextResponse = await fetch('https://ukies-api-search.eliteswitch.com/indexes/webshops_forms_entries/search', {
                                method: 'POST',
                                headers: {
                                    'Authorization': `Bearer ${apiToken}`,
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                    "q": null,
                                    "sort": ["id:desc"],
                                    "matchingStrategy": "all",
                                    "facets": ["*"],
                                    "filter": [[`form_id='${form.id}'`]],
                                    "limit": 90000,
                                    "offset": offset
                                })
                            });

                            if (!nextResponse.ok) {
                                break;
                            }

                            const nextData = await nextResponse.json();
                            const nextEntries = nextData.hits || [];
                            
                            if (nextEntries.length === 0) {
                                break;
                            }
                            
                            currentFormEntries = currentFormEntries.concat(nextEntries);
                            offset += nextEntries.length;
                        }
                    }
                    
                    // Process form entries and add optin data
                    const processedEntries = currentFormEntries.map(entry => {
                        const payload = typeof entry.payload === 'string' ? JSON.parse(entry.payload) : entry.payload;
                        
                        return {
                            email: payload.email || '',
                            optinTimestamp: new Date(entry.created_at).getTime(),
                            optinDate: new Date(entry.created_at).toISOString().split('T')[0],
                            optinForm: form.name,
                            optinSource: payload.source || form.name || 'Onbekend'
                        };
                    });
                    
                    allFormEntries = allFormEntries.concat(processedEntries);
                }
            }

            // Create email-based lookup for optin data
            const optinMap = {};
            allFormEntries.forEach(entry => {
                const email = entry.email?.toLowerCase();
                if (email) {
                    if (!optinMap[email] || entry.optinTimestamp > optinMap[email].optinTimestamp) {
                        optinMap[email] = entry;
                    }
                }
            });

            // Merge optin data with entries
            const entriesWithOptin = entries.map(entry => {
                const email = entry.email?.toLowerCase();
                const optinData = optinMap[email];
                
                if (optinData) {
                    return {
                        ...entry,
                        optin: 'Y',
                        optinDate: optinData.optinDate,
                        optinTimestamp: optinData.optinTimestamp,
                        optinForm: optinData.optinForm,
                        optinSource: optinData.optinSource
                    };
                } else {
                    return {
                        ...entry,
                        optin: 'N',
                        optinDate: '',
                        optinTimestamp: '',
                        optinForm: '',
                        optinSource: ''
                    };
                }
            });

            // Step 3.1: Filter out non-newsletter subscribers if required
            if (newsletterRequired) {
                setCsvProgress('Alleen nieuwsbrief inschrijvers behouden...');
                const filteredEntries = entriesWithOptin.filter(entry => entry.optin === 'Y');
                setCsvProgress(`${filteredEntries.length} records na nieuwsbrief filter.`);
                return filteredEntries;
            }

            return entriesWithOptin;
            
        } catch (error) {
            console.error('Error fetching newsletter data:', error);
            throw error;
        }
    };

    // Main CSV generation process
    const generateCSV = async (selectionData) => {
        try {
            setCsvGenerating(true);
            setCsvProgress('CSV generatie gestart...');
            
            const { selectionType, selectionParameters, fieldsEnabled, customersOnly, onlyLastOptin, name } = selectionData;
            
            if (selectionType === 'newsletter') {
                // Step 1: Fetch form data
                const formEntries = await fetchFormData(selectionParameters.forms);
                
                // Step 2: Deduplicate if needed
                const deduplicatedEntries = deduplicateData(formEntries, onlyLastOptin, fieldsEnabled);
                
                // Step 3: Fetch and merge customer data if needed
                const entriesWithCustomers = await fetchAndMergeCustomerData(deduplicatedEntries, fieldsEnabled);
                
                // Step 4: Filter customers only if needed
                const finalEntries = filterCustomersOnly(entriesWithCustomers, customersOnly);
                
                // Step 5: Generate CSV and upload
                const result = await generateAndUploadCSV(finalEntries, fieldsEnabled, name);
                
                // Add 5 seconds delay before completion
                setCsvProgress('CSV bestand succesvol gegenereerd! Download wordt gestart...');
                await new Promise(resolve => setTimeout(resolve, 3000));
                
                return result;
            }
            
            if (selectionType === 'specific_products') {
                // Step 1: Fetch sales lines data
                const salesLines = await fetchSalesLinesData(
                    selectionParameters.products,
                    selectionParameters.purchasedAfter,
                    selectionParameters.purchasedBefore,
                    selectionParameters.onlyLastProduct,
                    selectionParameters.productOperator
                );
                
                // Step 2 & 3: Merge sales lines with customer data
                const entriesWithCustomers = await mergeSalesWithCustomers(salesLines, fieldsEnabled);
                
                // Step 4: Fetch and merge newsletter data if needed
                const optinFieldsSelected = fieldsEnabled.some(field => 
                    ['optin', 'optinDate', 'optinSource'].includes(field)
                );
                const finalEntries = await fetchAndMergeOptinData(
                    entriesWithCustomers, 
                    fieldsEnabled, 
                    selectionParameters.optinRequired,
                    optinFieldsSelected
                );
                
                // Step 5: Generate CSV and upload
                const result = await generateAndUploadCSV(finalEntries, fieldsEnabled, name);
                
                // Add delay before completion
                setCsvProgress('CSV bestand succesvol gegenereerd! Download wordt gestart...');
                await new Promise(resolve => setTimeout(resolve, 3000));
                
                return result;
            }
            
            if (selectionType === 'form_entries') {
                // Fetch raw entries for the selected form
                const entries = await fetchFormEntriesRaw(selectionParameters.formId);

                // Generate CSV and upload, using the form's own field labels
                const result = await generateAndUploadCSV(entries, fieldsEnabled, name, selectionParameters.fieldLabels);

                // Add delay before completion
                setCsvProgress('CSV bestand succesvol gegenereerd! Download wordt gestart...');
                await new Promise(resolve => setTimeout(resolve, 3000));

                return result;
            }

            // TODO: Implement product_category selection type
            throw new Error('Alleen nieuwsbrief en product selecties worden momenteel ondersteund.');
            
        } catch (error) {
            console.error('CSV generation error:', error);
            throw error;
        } finally {
            setCsvGenerating(false);
            setCsvProgress('');
        }
    };

    // Fetch forms from API
    const fetchForms = async () => {
        try {
            const response = await fetch('https://ukies-api-search.eliteswitch.com/indexes/webshops_forms/search', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    "q": "",
                    "sort": ["id:asc"],
                    "matchingStrategy": "all",
                    "facets": ["*"],
                    "limit": 1000
                })
            });

            if (response.ok) {
                const data = await response.json();
                setForms(data.hits || []);
            } else {
                console.error('Failed to fetch forms:', response.statusText);
            }
        } catch (error) {
            console.error('Error fetching forms:', error);
        }
    };

    // Fetch categories from API
    const fetchCategories = async () => {
        try {
            const response = await fetch('https://ukies-api-search.eliteswitch.com/indexes/products_categories/search', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    "q": null,
                    "sort": ["id:desc"],
                    "matchingStrategy": "all",
                    "facets": ["*"],
                    "filter": [["category_status=1"]],
                    "limit": 1000
                })
            });

            if (response.ok) {
                const data = await response.json();
                setCategories(data.hits || []);
            } else {
                console.error('Failed to fetch categories:', response.statusText);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    // Fetch products from API
    const fetchProducts = async () => {
        try {
            const response = await fetch('https://ukies-api-search.eliteswitch.com/indexes/webshops_products/search', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    "q": null,
                    "sort": ["id:desc"],
                    "matchingStrategy": "all",
                    "facets": ["*"],
                    "filter": [["product_status = 'active'"]],
                    "limit": 1000
                })
            });

            if (response.ok) {
                const data = await response.json();
                setProducts(data.hits || []);
            } else {
                console.error('Failed to fetch products:', response.statusText);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    // Fetch selections from Firestore
    const fetchSelections = async () => {
        setLoading(true);
        try {
            const selectionsSnapshot = await getDocs(collection(db, 'selections'));
            const selectionsData = selectionsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            
            // Sort by last_download descending (most recent first, null values last)
            selectionsData.sort((a, b) => {
                // Handle null/undefined values - put them at the end
                if (!a.last_download && !b.last_download) {
                    // Both are null, sort by created_at as secondary
                    if (!a.created_at || !b.created_at) return 0;
                    const dateA = a.created_at.seconds ? a.created_at.seconds : new Date(a.created_at).getTime() / 1000;
                    const dateB = b.created_at.seconds ? b.created_at.seconds : new Date(b.created_at).getTime() / 1000;
                    return dateB - dateA;
                }
                if (!a.last_download) return 1; // a goes to end
                if (!b.last_download) return -1; // b goes to end
                
                // Both have last_download values, sort by most recent first
                const dateA = a.last_download.seconds ? a.last_download.seconds : new Date(a.last_download).getTime() / 1000;
                const dateB = b.last_download.seconds ? b.last_download.seconds : new Date(b.last_download).getTime() / 1000;
                return dateB - dateA;
            });
            
            setSelections(selectionsData);
        } catch (error) {
            console.error('Error fetching selections:', error);
            setShowAlert(true);
            setAlertMessage('Fout bij het ophalen van selecties. Probeer het opnieuw.');
            setAlertType('danger');
        } finally {
            setLoading(false);
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Handle selection type change with defaults
        if (name === 'type') {
            if (value === 'newsletter') {
                setFormData(prev => {
                    let updatedFields = prev.fieldsEnabled || [];
                    
                    // Add optin if not present
                    if (!updatedFields.includes('optin')) {
                        updatedFields = [...updatedFields, 'optin'];
                    }
                    
                    return {
                        ...prev,
                        [name]: value,
                        formsEnabled: ['2', '3', '4'], // Default selected forms
                        fieldsEnabled: updatedFields
                    };
                });
            } else if (value === 'form_entries') {
                // Form and its fields are form-specific, so start clean until a form is picked
                setFormData(prev => ({
                    ...prev,
                    [name]: value,
                    formsEnabled: [],
                    formId: '',
                    fieldsEnabled: []
                }));
                setFormFields([]);
            } else {
                // Clear forms and remove optin-related fields when switching to product types
                setFormData(prev => ({
                    ...prev,
                    [name]: value,
                    formsEnabled: [], // Clear forms for other types
                    formId: '',
                    fieldsEnabled: prev.fieldsEnabled?.filter(field => !['optin', 'optinDate', 'optinSource', 'purchasedProduct', 'purchaseDate'].includes(field)) || []
                }));
                setFormFields([]);
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    // Handle opening new selection modal
    const handleNewSelection = () => {
        setFormData({
            name: '',
            type: '',
            description: '',
            fieldsEnabled: ['firstname', 'lastname', 'email', 'city', 'country'], // Default fields (no optin initially)
            operator: 'any', // Default operator
            customersOnly: false, // Default checkbox state
            onlyLastOptin: true // Default checked
        });
        setShowModal(true);
    };

    // Handle closing modal
    const handleClose = () => {
        setShowModal(false);
        setFormData({});
        setShowFormsDropdown(false);
        setShowFieldsDropdown(false);
        setShowProductsDropdown(false);
        setProductSearchTerm('');
        setFormFields([]);
    };

    // Handle selecting the (single) form for the form_entries selection type, fetching its fields
    const handleFormEntriesFormSelect = async (e) => {
        const formId = e.target.value;

        setFormData(prev => ({ ...prev, formId, fieldsEnabled: [] }));
        setFormFields([]);

        if (!formId) return;

        setFormFieldsLoading(true);
        try {
            const response = await fetch('https://ukies-api-search.eliteswitch.com/indexes/webshops_forms_fields/search', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    "q": null,
                    "sort": ["id:asc"],
                    "matchingStrategy": "all",
                    "facets": ["*"],
                    "filter": [[`form_id='${formId}'`]],
                    "limit": 1000
                })
            });

            if (response.ok) {
                const data = await response.json();
                setFormFields(data.hits || []);
            } else {
                console.error('Failed to fetch form fields:', response.statusText);
            }
        } catch (error) {
            console.error('Error fetching form fields:', error);
        } finally {
            setFormFieldsLoading(false);
        }
    };

    // Handle row click to open view modal
    const handleRowClick = (selection) => {
        setSelectedSelection(selection);
        setViewFormData({
            name: selection.name || '',
            description: selection.description || '',
            type: selection.selectionType || '',
            fieldsEnabled: selection.fieldsEnabled || [],
            customersOnly: selection.customersOnly || false,
            onlyLastOptin: selection.onlyLastOptin || false
        });
        setDeleteEnabled(false);
        setShowViewModal(true);
    };

    // Handle closing view modal
    const handleViewClose = () => {
        setShowViewModal(false);
        setSelectedSelection(null);
        setViewFormData({});
        setDeleteEnabled(false);
    };

    // Handle view form input changes (only for name and description)
    const handleViewInputChange = (e) => {
        const { name, value } = e.target;
        setViewFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle save changes to selection
    const handleSaveChanges = async () => {
        if (!selectedSelection || !selectedSelection.id) return;

        setSaving(true);
        try {
            await updateDoc(doc(db, 'selections', selectedSelection.id), {
                name: viewFormData.name,
                description: viewFormData.description
            });

            // Update local state
            setSelections(prev => prev.map(s =>
                s.id === selectedSelection.id
                    ? { ...s, name: viewFormData.name, description: viewFormData.description }
                    : s
            ));

            setShowAlert(true);
            setAlertMessage('Selectie succesvol bijgewerkt!');
            setAlertType('success');
            setShowViewModal(false);
        } catch (error) {
            console.error('Error updating selection:', error);
            setShowAlert(true);
            setAlertMessage('Fout bij het bijwerken van de selectie. Probeer het opnieuw.');
            setAlertType('danger');
        } finally {
            setSaving(false);
        }
    };

    // Handle CSV download
    const handleDownload = async () => {
        if (!selectedSelection || !selectedSelection.fileLink) return;

        setDownloading(true);
        try {
            // Extract filename from the fileLink URL
            const url = new URL(selectedSelection.fileLink);
            const pathSegments = url.pathname.split('/');
            const filename = pathSegments[pathSegments.length - 1].split('?')[0]; // Remove query parameters

            // Trigger download
            triggerFileDownload(selectedSelection.fileLink, filename);

            // Get current timestamp for immediate UI update
            const currentTimestamp = new Date();

            // Update last_download timestamp in Firestore
            await updateDoc(doc(db, 'selections', selectedSelection.id), {
                last_download: serverTimestamp()
            });

            // Update local state with current timestamp for immediate UI feedback
            setSelections(prev => prev.map(s =>
                s.id === selectedSelection.id
                    ? { ...s, last_download: currentTimestamp }
                    : s
            ));

            // Also update the selectedSelection state for the current modal
            setSelectedSelection(prev => ({
                ...prev,
                last_download: currentTimestamp
            }));

            setShowAlert(true);
            setAlertMessage('CSV bestand gedownload!');
            setAlertType('success');
        } catch (error) {
            console.error('Error downloading file:', error);
            setShowAlert(true);
            setAlertMessage('Fout bij het downloaden van het bestand.');
            setAlertType('danger');
        } finally {
            setDownloading(false);
        }
    };

    // Handle delete selection
    const handleDeleteSelection = async () => {
        if (!selectedSelection || !selectedSelection.id) return;

        const confirmMessage = `Weet je zeker dat je de selectie "${selectedSelection.name}" wilt verwijderen?\n\nDit zal ook het CSV bestand permanent verwijderen.\n\nDeze actie kan niet ongedaan worden gemaakt!`;

        if (!window.confirm(confirmMessage)) return;

        setDeleting(true);
        try {
            // Delete the file from Firebase Storage if it exists
            if (selectedSelection.fileLink) {
                try {
                    console.log('Attempting to delete file from storage:', selectedSelection.fileLink);
                    
                    // Extract the file path from Firebase Storage download URL
                    const url = new URL(selectedSelection.fileLink);
                    const pathMatch = url.pathname.match(/\/o\/(.+)$/);
                    
                    if (pathMatch) {
                        // Decode the URL-encoded path
                        const encodedPath = pathMatch[1];
                        const decodedPath = decodeURIComponent(encodedPath);
                        
                        console.log('Extracted file path:', decodedPath);
                        
                        const storage = getStorage();
                        const fileRef = ref(storage, decodedPath);
                        
                        await deleteObject(fileRef);
                        console.log('File successfully deleted from storage:', decodedPath);
                    } else {
                        console.warn('Could not extract file path from URL:', selectedSelection.fileLink);
                    }
                    
                } catch (storageError) {
                    console.error('Error deleting file from storage:', storageError);
                    console.error('File URL was:', selectedSelection.fileLink);
                    // Continue with document deletion even if file deletion fails
                }
            }

            // Delete the document from Firestore
            await deleteDoc(doc(db, 'selections', selectedSelection.id));

            // Update local state
            setSelections(prev => prev.filter(s => s.id !== selectedSelection.id));

            setShowViewModal(false);
            setSelectedSelection(null);
            setShowAlert(true);
            setAlertMessage('Selectie succesvol verwijderd!');
            setAlertType('success');
        } catch (error) {
            console.error('Error deleting selection:', error);
            setShowAlert(true);
            setAlertMessage('Fout bij het verwijderen van de selectie. Probeer het opnieuw.');
            setAlertType('danger');
        } finally {
            setDeleting(false);
        }
    };

    // Handle form toggle for multiselect
    const handleFormToggle = (formId, isChecked) => {
        setFormData(prev => {
            const currentFormsEnabled = prev.formsEnabled || [];
            
            if (isChecked) {
                // Add form if not already present
                if (!currentFormsEnabled.includes(formId)) {
                    return { ...prev, formsEnabled: [...currentFormsEnabled, formId] };
                }
            } else {
                // Remove form if present
                return { ...prev, formsEnabled: currentFormsEnabled.filter(id => id !== formId) };
            }
            
            return prev;
        });
    };

    // Handle field toggle for multiselect
    const handleFieldToggle = (fieldId, isChecked) => {
        setFormData(prev => {
            const currentFieldsEnabled = prev.fieldsEnabled || [];
            
            if (isChecked) {
                // Prevent optin-related fields from being selected if optin is not selected
                if ((fieldId === 'optinSource' || fieldId === 'optinDate') && !currentFieldsEnabled.includes('optin')) {
                    return prev; // Don't allow selection
                }
                
                // Prevent purchaseDate from being selected if purchasedProduct is not selected
                if (fieldId === 'purchaseDate' && !currentFieldsEnabled.includes('purchasedProduct')) {
                    return prev; // Don't allow selection
                }
                
                // Add field if not already present
                if (!currentFieldsEnabled.includes(fieldId)) {
                    return { ...prev, fieldsEnabled: [...currentFieldsEnabled, fieldId] };
                }
            } else {
                // Remove field if present
                let updatedFields = currentFieldsEnabled.filter(id => id !== fieldId);
                
                // If optin is being unchecked, also uncheck optinSource and optinDate
                if (fieldId === 'optin') {
                    updatedFields = updatedFields.filter(id => !['optinSource', 'optinDate'].includes(id));
                }
                
                // If purchasedProduct is being unchecked, also uncheck purchaseDate
                if (fieldId === 'purchasedProduct') {
                    updatedFields = updatedFields.filter(id => id !== 'purchaseDate');
                }
                
                return { ...prev, fieldsEnabled: updatedFields };
            }
            
            return prev;
        });
    };

    // Handle product toggle for multiselect
    const handleProductToggle = (productId, isChecked) => {
        setFormData(prev => {
            const currentProductsEnabled = prev.productsEnabled || [];
            
            if (isChecked) {
                // Add product if not already present
                if (!currentProductsEnabled.includes(productId)) {
                    return { ...prev, productsEnabled: [...currentProductsEnabled, productId] };
                }
            } else {
                // Remove product if present
                const updatedProducts = currentProductsEnabled.filter(id => id !== productId);
                return { ...prev, productsEnabled: updatedProducts };
            }
            
            return prev;
        });
    };

    // Handle save selection
    const handleSave = async () => {
        if (!formData.name || !formData.type) {
            setShowAlert(true);
            setAlertMessage('Vul alle verplichte velden in.');
            setAlertType('danger');
            return;
        }

        // Validate newsletter selection has forms selected
        if (formData.type === 'newsletter' && (!formData.formsEnabled || formData.formsEnabled.length === 0)) {
            setShowAlert(true);
            setAlertMessage('Selecteer minimaal één nieuwsbrief formulier.');
            setAlertType('danger');
            return;
        }

        // Validate specific products selection has products selected
        if (formData.type === 'specific_products' && (!formData.productsEnabled || formData.productsEnabled.length === 0)) {
            setShowAlert(true);
            setAlertMessage('Selecteer minimaal één product.');
            setAlertType('danger');
            return;
        }

        // Validate form entries selection has a form selected
        if (formData.type === 'form_entries' && !formData.formId) {
            setShowAlert(true);
            setAlertMessage('Selecteer een formulier.');
            setAlertType('danger');
            return;
        }

        // Validate field selection
        if (!formData.fieldsEnabled || formData.fieldsEnabled.length === 0) {
            setShowAlert(true);
            setAlertMessage('Selecteer minimaal één veld om te exporteren.');
            setAlertType('danger');
            return;
        }

        setSaving(true);
        let selectionDocId = null;
        
        try {
            // Prepare selection parameters based on type
            let selectionParameters = {
                categories: [],
                forms: [],
                products: []
            };

            if (formData.type === 'specific_products' && formData.productsEnabled) {
                selectionParameters.products = formData.productsEnabled;
                selectionParameters.productOperator = formData.productOperator || 'any';
                // Add optional date filters
                if (formData.purchasedAfter) {
                    selectionParameters.purchasedAfter = formData.purchasedAfter;
                }
                if (formData.purchasedBefore) {
                    selectionParameters.purchasedBefore = formData.purchasedBefore;
                }
                // Add optin requirement and last product setting
                selectionParameters.optinRequired = formData.optinRequired || false;
                selectionParameters.onlyLastProduct = formData.onlyLastProduct !== undefined ? formData.onlyLastProduct : true;
            } else if (formData.type === 'product_category' && formData.categoryId) {
                selectionParameters.categories = [formData.categoryId.trim()];
                // Add optin requirement and last product setting for product_category too
                selectionParameters.optinRequired = formData.optinRequired || false;
                selectionParameters.onlyLastProduct = formData.onlyLastProduct !== undefined ? formData.onlyLastProduct : true;
            } else if (formData.type === 'newsletter' && formData.formsEnabled) {
                selectionParameters.forms = formData.formsEnabled;
                selectionParameters.operator = formData.operator || 'any';
            } else if (formData.type === 'form_entries' && formData.formId) {
                selectionParameters.forms = [formData.formId];
                selectionParameters.formId = formData.formId;
                // Snapshot field labels at creation time so the view modal and CSV headers
                // stay correct even if the form's fields are later renamed/removed
                selectionParameters.fieldLabels = formFields.reduce((acc, f) => {
                    acc[f.name] = f.label || prettifyFieldName(f.name);
                    return acc;
                }, {});
            }

            // Create selection document
            const selectionData = {
                name: formData.name,
                description: formData.description || '',
                selectionType: formData.type,
                selectionParameters,
                fieldsEnabled: formData.fieldsEnabled || [],
                customersOnly: formData.customersOnly || false,
                onlyLastOptin: formData.onlyLastOptin || false,
                created_at: serverTimestamp(),
                created_by: (user?.firstName && user?.lastName) 
                    ? `${user.firstName} ${user.lastName}` 
                    : user?.email || 'Unknown',
                last_download: null,
                result: null,
                fileLink: null
            };

            // Add to Firestore
            const docRef = await addDoc(collection(db, 'selections'), selectionData);
            selectionDocId = docRef.id;
            
            // Generate CSV and update document
            try {
                const csvResult = await generateCSV(selectionData);
                
                // Trigger automatic file download
                if (csvResult.fileLink && csvResult.filename) {
                    console.log('Triggering download for selection:', selectionDocId, 'with file:', csvResult.filename);
                    triggerFileDownload(csvResult.fileLink, csvResult.filename);
                    
                    // Update the document with CSV results AND last_download timestamp in a single operation
                    console.log('About to update document with results and timestamp...');
                    await updateDoc(doc(db, 'selections', selectionDocId), {
                        result: csvResult.recordCount,
                        fileLink: csvResult.fileLink,
                        last_download: serverTimestamp()
                    });
                    console.log('Completed document update with results and timestamp');
                } else {
                    // If no file download, just update results
                    await updateDoc(doc(db, 'selections', selectionDocId), {
                        result: csvResult.recordCount,
                        fileLink: csvResult.fileLink
                    });
                }
                
                // Add a small delay before refreshing to ensure Firestore update has propagated
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                // Refresh selections list
                fetchSelections();
                
                // Close create modal and clear form data
                setShowModal(false);
                setFormData({});
                
                // Create the newly created selection object with all the data
                const newlyCreatedSelection = {
                    id: selectionDocId,
                    ...selectionData,
                    result: csvResult.recordCount,
                    fileLink: csvResult.fileLink,
                    // Convert serverTimestamp to current date for immediate display
                    created_at: new Date(),
                    last_download: new Date() // Since it was automatically downloaded
                };
                
                // Open view modal for the newly created selection
                handleRowClick(newlyCreatedSelection);
                
                setShowAlert(true);
                setAlertMessage(`Selectie succesvol aangemaakt! ${csvResult.recordCount} records gegenereerd en automatisch gedownload.`);
                setAlertType('success');
                
            } catch (csvError) {
                console.error('CSV generation error:', csvError);
                
                // Update document with error status
                await updateDoc(doc(db, 'selections', selectionDocId), {
                    result: 0,
                    fileLink: null,
                    error: csvError.message || 'Fout bij CSV generatie'
                });
                
                // Refresh selections list
                fetchSelections();
                
                // Close create modal and clear form data
                setShowModal(false);
                setFormData({});
                
                // Create the selection object even though CSV generation failed
                const newlyCreatedSelection = {
                    id: selectionDocId,
                    ...selectionData,
                    result: 0,
                    fileLink: null,
                    // Convert serverTimestamp to current date for immediate display
                    created_at: new Date(),
                    last_download: null,
                    error: csvError.message || 'Fout bij CSV generatie'
                };
                
                // Open view modal for the newly created selection
                handleRowClick(newlyCreatedSelection);
                
                setShowAlert(true);
                setAlertMessage(`Selectie aangemaakt maar CSV generatie gefaald: ${csvError.message}`);
                setAlertType('warning');
            }
            
        } catch (error) {
            console.error('Error saving selection:', error);
            setShowAlert(true);
            setAlertMessage('Fout bij het aanmaken van de selectie. Probeer het opnieuw.');
            setAlertType('danger');
        } finally {
            setSaving(false);
        }
    };

    // Auto-hide alert after 5 seconds
    useEffect(() => {
        if (showAlert) {
            const timer = setTimeout(() => {
                setShowAlert(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [showAlert]);

    // Add custom styles for dropdown alignment
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            /* Custom dropdown styles */
            #forms-dropdown, #fields-dropdown, #products-dropdown {
                justify-content: space-between;
                align-items: center;
            }
            #forms-dropdown::after, #fields-dropdown::after, #products-dropdown::after {
                margin-left: auto;
            }
            #forms-dropdown .flex-grow-1, #fields-dropdown .flex-grow-1, #products-dropdown .flex-grow-1 {
                text-align: left;
            }
            .dropdown-toggle.show {
                border-color: #86b7fe;
                box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
            }
            
            /* Enhanced scrollbar for dropdown menus */
            .dropdown-menu {
                scrollbar-width: auto;
                scrollbar-color: #495057 #e9ecef;
            }
            
            .dropdown-menu::-webkit-scrollbar {
                width: 10px;
            }
            
            .dropdown-menu::-webkit-scrollbar-track {
                background: #f8f9fa;
                border-radius: 6px;
                margin: 4px 0;
            }
            
            .dropdown-menu::-webkit-scrollbar-thumb {
                background: #495057;
                border-radius: 6px;
                border: 2px solid #f8f9fa;
                min-height: 20px;
            }
            
            .dropdown-menu::-webkit-scrollbar-thumb:hover {
                background: #343a40;
            }
            
            /* Ensure scrollbar is visible when dropdown has maxHeight */
            .dropdown-menu[style*="maxHeight"] {
                overflow-y: scroll !important;
                scrollbar-gutter: stable;
            }
            
            /* Custom checkbox styling for form checkboxes only */
            .large-checkbox-container .form-check-input[type="checkbox"] {
                width: 1.5em !important;
                height: 1.5em !important;
            }
            
            .large-checkbox-container {
                display: flex !important;
                align-items: center !important;
                gap: 0.5rem !important;
            }
            
            .large-checkbox-container .form-check-input:checked {
                background-color: var(--ukies-light-green);
                border-color: var(--ukies-light-green);
            }
            .large-checkbox-container .form-check-input:focus {
                border-color: var(--ukies-light-green);
                box-shadow: 0 0 0 0.25rem rgba(var(--ukies-light-green-rgb, 40, 167, 69), 0.25);
            }
            
            /* Delete switch styling - match UkiesAdminSeries pattern */
            .custom-switch-large {
                transform: scale(1.5);
                transform-origin: left center;
            }
            .custom-switch-large .form-check-input:checked {
                background-color: #28a745 !important;
                border-color: #28a745 !important;
            }
            .custom-switch-large .form-check-input:not(:checked) {
                background-color: #dc3545 !important;
                border-color: #dc3545 !important;
            }
            .custom-switch-large .form-check-input:focus {
                box-shadow: none !important;
            }
            .custom-switch-large .form-check-input:checked:focus {
                background-color: #28a745 !important;
                border-color: #28a745 !important;
                box-shadow: none !important;
            }
            .custom-switch-large .form-check-input:not(:checked):focus {
                background-color: #dc3545 !important;
                border-color: #dc3545 !important;
                box-shadow: none !important;
            }
            .custom-switch-large .form-check-label {
                font-size: 0.7rem !important;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Auth state listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                // User is signed in
                setAuthReady(true);
            } else {
                // User is signed out
                localStorage.removeItem('ukies-webinars-admin')
                localStorage.removeItem('ukies-webinars-accessToken')
                setToken()
                setUser()
                setAuthReady(false);
            }
        })
        
        return () => unsubscribe(); // Cleanup listener
    }, []);

    // Fetch selections when auth is ready and selections is empty
    useEffect(() => {
        if (authReady && selections.length === 0) {
            fetchSelections();
        }
    }, [authReady, selections.length]);

    // Fetch forms only if empty
    useEffect(() => {
        if (forms.length === 0) {
            fetchForms();
        }
    }, [forms.length]);

    // Fetch categories only if empty
    useEffect(() => {
        if (categories.length === 0) {
            fetchCategories();
        }
    }, [categories.length]);

    // Fetch products only if empty
    useEffect(() => {
        if (products.length === 0) {
            fetchProducts();
        }
    }, [products.length]);

    if(!token) {
        return <Stack><Login user={user} setUser={setUser} token={token} setToken={setToken} /></Stack>
    }

    return (
        <>
            <Navigation user={user} currentPage='selections' />
            {showAlert && (
                <Alert 
                    variant={alertType} 
                    dismissible 
                    onClose={() => setShowAlert(false)} 
                    style={{
                        position: 'fixed',
                        top: '15px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1060,
                        minWidth: '300px',
                        maxWidth: '600px'
                    }}
                >
                    {alertMessage}
                </Alert>
            )}
            
            <Stack style={{marginTop:'50px',padding:'1rem 0'}}>
                <h1 style={{fontSize:'2.5rem'}}>Selecties</h1>
                <div style={{ color:'#6c757d'}}>
                    <p><small className="text-muted">Deze pagina is in ontwikkeling.</small></p>
                </div>
                
                {/* Action Buttons */}
                <div className="d-flex justify-content-end mb-3 gap-2" style={{marginTop:'-2.5rem'}}>
                    <Button
                        variant="success"
                        size="sm"
                        disabled={loading}
                        onClick={handleNewSelection}
                        title="Klik om een nieuwe selectie toe te voegen"
                        style={{
                            backgroundColor: 'var(--ukies-light-green)',
                            borderColor: 'var(--ukies-light-green)',
                            color: 'white'
                        }}
                    >
                        + Nieuwe selectie
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={loading}
                        onClick={fetchSelections}
                        title="Klik om data te verversen"
                        style={{
                            backgroundColor: '#3b4f46',
                            borderColor: '#3b4f46'
                        }}
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Verversen...
                            </>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-clockwise me-2" viewBox="0 0 16 16">
                                    <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                                    <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                                </svg>
                                Refresh
                            </>
                        )}
                    </Button>
                </div>
                <Table striped hover style={{cursor:'pointer'}}>
                    <thead>
                        <tr>
                            <th>Naam</th>
                            <th>Type</th>
                            <th>Parameters</th>
                            <th>Aantal</th>
                            <th>Aangemaakt op</th>
                            <th>Aangemaakt door</th>
                            <th>Laatst gedownload op</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="7" style={{textAlign:'center', padding:'2rem', color:'#6c757d'}}>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Selecties laden...
                                </td>
                            </tr>
                        ) : selections.length === 0 ? (
                            <tr>
                                <td colSpan="7" style={{textAlign:'center', padding:'2rem', color:'#6c757d'}}>
                                    Geen selecties gevonden
                                </td>
                            </tr>
                        ) : (
                            selections.map((selection, index) => (
                                <tr key={selection.id || index} onClick={() => handleRowClick(selection)}>
                                    <td>
                                        <strong>{selection.name}</strong>
                                        {selection.description && (
                                            <div style={{fontSize:'0.8em', color:'#6c757d', marginTop:'2px'}}>
                                                {selection.description}
                                            </div>
                                        )}
                                    </td>
                                    <td>{translateSelectionType(selection.selectionType)}</td>
                                    <td style={{fontSize:'0.9em'}}>
                                        {formatSelectionParameters(selection.selectionParameters)}
                                    </td>
                                    <td style={{fontSize:'0.9em', textAlign:'right'}}>
                                        {formatNumber(selection.result)}
                                    </td>
                                    <td style={{fontSize:'0.9em'}}>
                                        {formatDate(selection.created_at)}
                                    </td>
                                    <td style={{fontSize:'0.9em'}}>
                                        {selection.created_by || ''}
                                    </td>
                                    <td style={{fontSize:'0.9em'}}>
                                        {formatDate(selection.last_download) || 'Nooit'}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </Stack>

            {/* New Selection Modal */}
            <Modal show={showModal} onHide={handleClose} size="lg" backdrop={csvGenerating ? "static" : true} keyboard={!csvGenerating}>
                <Modal.Header closeButton={!csvGenerating}>
                    <Modal.Title>Nieuwe selectie aanmaken</Modal.Title>
                </Modal.Header>
                
                {/* CSV Generation Loading Overlay */}
                {csvGenerating && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        zIndex: 1050,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '0.5rem'
                    }}>
                        <div className="text-center">
                            <div className="spinner-border" role="status" style={{ width: '3rem', height: '3rem', color: 'var(--ukies-light-green)' }}>
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <h5 className="mt-3 mb-2">CSV bestand genereren...</h5>
                            <p className="text-muted">{csvProgress}</p>
                            <div className="mt-3">
                                <small className="text-muted">Dit kan even duren. Sluit dit venster niet af.</small>
                            </div>
                        </div>
                    </div>
                )}
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Naam van de selectie <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name || ''}
                                onChange={handleInputChange}
                                placeholder="Voer een naam in voor deze selectie..."
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Beschrijving (optioneel)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="description"
                                value={formData.description || ''}
                                onChange={handleInputChange}
                                placeholder="Optionele beschrijving van de selectie..."
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Type selectie <span className="text-danger">*</span></Form.Label>
                            <Form.Select
                                name="type"
                                value={formData.type || ''}
                                onChange={handleInputChange}
                            >
                                <option value="">Selecteer type selectie...</option>
                                <option value="newsletter">Nieuwsbrief inschrijvingen</option>
                                <option value="specific_products">Product aankopen</option>
                                <option value="product_category">Categorie aankopen</option>
                                <option value="form_entries">Formulier inzendingen</option>
                            </Form.Select>
                        </Form.Group>

                        {/* Conditional form fields based on selection type */}
                        {formData.type === 'newsletter' && (
                            <div className="mt-3">
                                <Form.Group className="mb-3">
                                    <Form.Label>Formulieren <span className="text-danger">*</span></Form.Label>
                                    <Dropdown 
                                        onSelect={(e) => e && e.preventDefault()}
                                        autoClose={false}
                                        show={showFormsDropdown}
                                        onToggle={setShowFormsDropdown}
                                    >
                                        <Dropdown.Toggle 
                                            variant="outline-secondary" 
                                            id="forms-dropdown"
                                            className="w-100 text-start d-flex"
                                            disabled={(() => {
                                                if (forms.length === 0) return true;
                                                const newsletterForms = forms.filter(form => {
                                                    const formName = form.name || form.title || `Form ${form.id}`;
                                                    return formName.toLowerCase().includes('nieuwsbrief inschrijvingen');
                                                });
                                                return newsletterForms.length === 0;
                                            })()}
                                        >
                                            <span className="flex-grow-1">
                                                {(() => {
                                                    if (forms.length === 0) return 'Formulieren laden...';
                                                    const newsletterForms = forms.filter(form => {
                                                        const formName = form.name || form.title || `Form ${form.id}`;
                                                        return formName.toLowerCase().includes('nieuwsbrief inschrijvingen');
                                                    });
                                                    if (newsletterForms.length === 0) return 'Geen nieuwsbrief formulieren beschikbaar';
                                                    return formData.formsEnabled && formData.formsEnabled.length > 0 
                                                        ? `${formData.formsEnabled.length} formulier(en) geselecteerd`
                                                        : 'Selecteer formulier(en)...';
                                                })()}
                                            </span>
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu className="w-100" style={{ maxHeight: '340px', overflowY: 'scroll' }}>
                                            {(() => {
                                                if (forms.length === 0) {
                                                    return <Dropdown.Item disabled>Geen formulieren beschikbaar</Dropdown.Item>;
                                                } else {
                                                    // Filter forms to only show those with "Nieuwsbrief inschrijvingen" in the name
                                                    const newsletterForms = forms.filter(form => {
                                                        const formName = form.name || form.title || `Form ${form.id}`;
                                                        return formName.toLowerCase().includes('nieuwsbrief inschrijvingen');
                                                    });
                                                    
                                                    if (newsletterForms.length === 0) {
                                                        return <Dropdown.Item disabled>Geen nieuwsbrief formulieren beschikbaar</Dropdown.Item>;
                                                    }
                                                    
                                                    return newsletterForms.map((form) => {
                                                        const isSelected = formData.formsEnabled?.includes(form.id.toString()) || false;
                                                        return (
                                                            <Dropdown.Item 
                                                                key={form.id} 
                                                                as="div" 
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handleFormToggle(form.id.toString(), !isSelected);
                                                                }}
                                                                className="d-flex align-items-center"
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <span 
                                                                    style={{ 
                                                                        width: '20px', 
                                                                        textAlign: 'center', 
                                                                        color: isSelected ? '#28a745' : 'transparent',
                                                                        fontSize: '1.1rem',
                                                                        marginRight: '8px'
                                                                    }}
                                                                >
                                                                    ✓
                                                                </span>
                                                                <span className="flex-grow-1">{form.name || form.title || `Form ${form.id}`}</span>
                                                            </Dropdown.Item>
                                                        );
                                                    });
                                                }
                                            })()}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    
                                    {/* Display selected forms */}
                                    {formData.formsEnabled && formData.formsEnabled.length > 0 && (
                                        <div className="mt-3">
                                            <Form.Label>Geselecteerde formulieren:</Form.Label>
                                            <div className="d-flex flex-wrap gap-2">
                                                {(() => {
                                                    // Get newsletter forms in dropdown order (same logic as dropdown)
                                                    const newsletterForms = forms.filter(form => {
                                                        const formName = form.name || form.title || `Form ${form.id}`;
                                                        return formName.toLowerCase().includes('nieuwsbrief inschrijvingen');
                                                    });
                                                    
                                                    // Filter to only show selected forms, maintaining dropdown order
                                                    return newsletterForms
                                                        .filter(form => formData.formsEnabled.includes(form.id.toString()))
                                                        .map((formItem) => (
                                                            <span 
                                                                key={formItem.id} 
                                                                className="badge d-flex align-items-center"
                                                                style={{ 
                                                                    fontSize: '0.8rem',
                                                                    backgroundColor: 'var(--ukies-light-green)',
                                                                    color: 'white',
                                                                    padding: '0.5rem 0.75rem',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                <span style={{ marginRight: '0.5rem' }}>
                                                                    {formItem.name || formItem.title || `Form ${formItem.id}`}
                                                                </span>
                                                                <span 
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        handleFormToggle(formItem.id.toString(), false);
                                                                    }}
                                                                    style={{ 
                                                                        cursor: 'pointer',
                                                                        fontSize: '1rem',
                                                                        fontWeight: 'bold',
                                                                        opacity: 0.8,
                                                                        marginLeft: 'auto',
                                                                        marginTop: '-2px'
                                                                    }}
                                                                    title="Verwijderen"
                                                                >
                                                                    ×
                                                                </span>
                                                            </span>
                                                        ));
                                                })()}
                                            </div>
                                        </div>
                                    )}

                                    {/* Operator dropdown - only show if multiple forms selected */}
                                    {formData.formsEnabled && formData.formsEnabled.length > 1 && (
                                        <div className="mt-3">
                                            <Form.Label>Selectie modus</Form.Label>
                                            <Form.Select
                                                name="operator"
                                                value={formData.operator || 'any'}
                                                onChange={handleInputChange}
                                                disabled
                                            >
                                                <option value="any">Ingeschreven op minstens één van de formulieren</option>
                                                <option value="all">Ingeschreven op alle formulieren</option>
                                            </Form.Select>
                                        </div>
                                    )}

                                    {/* Only last optin checkbox */}
                                    <div className="mt-3">
                                        <Form.Check
                                            type="checkbox"
                                            id="onlyLastOptin"
                                            name="onlyLastOptin"
                                            checked={formData.onlyLastOptin || false}
                                            onChange={handleInputChange}
                                            className="large-checkbox-container"
                                            label={
                                                <span className="d-flex align-items-center gap-1">
                                                    Alleen de laatste inschrijving tonen
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip>
                                                                Als je voor deze optie kiest wordt de selectie ontdubbeld op emailadres. Alleen de laatste inschrijving per emailadres wordt opgenomen in de selectie.
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '16px',
                                                                height: '16px',
                                                                borderRadius: '50%',
                                                                backgroundColor: 'var(--ukies-dark-orange)',
                                                                color: 'white',
                                                                fontSize: '10px',
                                                                fontWeight: 'bold',
                                                                cursor: 'help',
                                                                marginTop: '-5px'
                                                            }}
                                                        >
                                                            i
                                                        </span>
                                                    </OverlayTrigger>
                                                </span>
                                            }
                                        />
                                    </div>

                                    {/* Customers only checkbox */}
                                    <div className="mt-3">
                                        <Form.Check
                                            type="checkbox"
                                            id="customersOnly"
                                            name="customersOnly"
                                            checked={formData.customersOnly || false}
                                            onChange={handleInputChange}
                                            className="large-checkbox-container"
                                            label={
                                                <span className="d-flex align-items-center gap-1">
                                                    Alleen klanten in de selectie
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip>
                                                                Als je voor deze optie kiest worden alleen klanten opgenomen in de selectie. Alle inschrijvingen waarvan het emailadres niet gematched kan worden aan een klant worden uit de selectie verwijderd.
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '16px',
                                                                height: '16px',
                                                                borderRadius: '50%',
                                                                backgroundColor: 'var(--ukies-dark-orange)',
                                                                color: 'white',
                                                                fontSize: '10px',
                                                                fontWeight: 'bold',
                                                                cursor: 'help',
                                                                marginTop: '-5px'
                                                            }}
                                                        >
                                                            i
                                                        </span>
                                                    </OverlayTrigger>
                                                </span>
                                            }
                                        />
                                    </div>
                                </Form.Group>
                            </div>
                        )}

                        {formData.type === 'specific_products' && (
                            <div className="mt-3">
                                <Form.Group className="mb-3">
                                    <Form.Label>Producten <span className="text-danger">*</span></Form.Label>
                                    
                                    {/* Products multiselect dropdown with search */}
                                    <Dropdown 
                                        onSelect={(e) => e && e.preventDefault()}
                                        autoClose={false}
                                        show={showProductsDropdown}
                                        onToggle={setShowProductsDropdown}
                                    >
                                        <Dropdown.Toggle 
                                            variant="outline-secondary" 
                                            id="products-dropdown"
                                            className="w-100 text-start d-flex"
                                        >
                                            <span className="flex-grow-1">
                                                {formData.productsEnabled && formData.productsEnabled.length > 0 
                                                    ? `${formData.productsEnabled.length} product(en) geselecteerd`
                                                    : 'Selecteer product(en)...'}
                                            </span>
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu className="w-100" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                            {/* Search input */}
                                            <div className="p-2">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Zoek producten..."
                                                    value={productSearchTerm}
                                                    onChange={(e) => setProductSearchTerm(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                            <Dropdown.Divider />
                                            
                                            {(() => {
                                                // Sort products by title ascending and filter by search term
                                                const filteredProducts = products
                                                    .filter(product => {
                                                        const productTitle = product.title || `Product ${product.id}`;
                                                        return productSearchTerm === '' || 
                                                               productTitle.toLowerCase().includes(productSearchTerm.toLowerCase());
                                                    })
                                                    .sort((a, b) => {
                                                        const titleA = (a.title || `Product ${a.id}`).toLowerCase();
                                                        const titleB = (b.title || `Product ${b.id}`).toLowerCase();
                                                        return titleA.localeCompare(titleB);
                                                    });
                                                
                                                if (filteredProducts.length === 0) {
                                                    return (
                                                        <Dropdown.Item disabled>
                                                            {productSearchTerm ? 'Geen producten gevonden' : 'Geen producten beschikbaar'}
                                                        </Dropdown.Item>
                                                    );
                                                }
                                                
                                                return filteredProducts.map((product) => {
                                                    const isSelected = formData.productsEnabled?.includes(product.id.toString()) || false;
                                                    return (
                                                        <Dropdown.Item 
                                                            key={product.id} 
                                                            as="div" 
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleProductToggle(product.id.toString(), !isSelected);
                                                            }}
                                                            className="d-flex align-items-center"
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <span 
                                                                style={{ 
                                                                    width: '20px', 
                                                                    textAlign: 'center', 
                                                                    color: isSelected ? '#28a745' : 'transparent',
                                                                    fontSize: '1.1rem',
                                                                    marginRight: '8px'
                                                                }}
                                                            >
                                                                ✓
                                                            </span>
                                                            <span className="flex-grow-1">{product.title || `Product ${product.id}`}</span>
                                                        </Dropdown.Item>
                                                    );
                                                });
                                            })()}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    
                                    {/* Display selected products as badges */}
                                    {formData.productsEnabled && formData.productsEnabled.length > 0 && (
                                        <div className="mt-3">
                                            <Form.Label>Geselecteerde producten:</Form.Label>
                                            <div className="d-flex flex-wrap gap-2">
                                                {(() => {
                                                    // Create ordered products array maintaining alphabetical order
                                                    const orderedProducts = products
                                                        .filter(product => formData.productsEnabled.includes(product.id.toString()))
                                                        .sort((a, b) => {
                                                            const titleA = (a.title || `Product ${a.id}`).toLowerCase();
                                                            const titleB = (b.title || `Product ${b.id}`).toLowerCase();
                                                            return titleA.localeCompare(titleB);
                                                        });
                                                    
                                                    return orderedProducts.map((product) => (
                                                        <span 
                                                            key={product.id} 
                                                            className="badge d-flex align-items-center"
                                                            style={{ 
                                                                fontSize: '0.8rem',
                                                                backgroundColor: 'var(--ukies-light-green)',
                                                                color: 'white',
                                                                padding: '0.5rem 0.75rem',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <span style={{ marginRight: '0.5rem' }}>
                                                                {product.title || `Product ${product.id}`}
                                                            </span>
                                                            <span 
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handleProductToggle(product.id.toString(), false);
                                                                }}
                                                                style={{ 
                                                                    cursor: 'pointer',
                                                                    fontSize: '1rem',
                                                                    fontWeight: 'bold',
                                                                    opacity: 0.8,
                                                                    marginLeft: 'auto',
                                                                    marginTop: '-2px'
                                                                }}
                                                                title="Verwijderen"
                                                            >
                                                                ×
                                                            </span>
                                                        </span>
                                                    ));
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </Form.Group>

                                {/* Product selection mode dropdown - only show if multiple products selected */}
                                {formData.productsEnabled && formData.productsEnabled.length > 1 && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Selectie modus</Form.Label>
                                        <Form.Select
                                            name="productOperator"
                                            value={formData.productOperator || 'any'}
                                            onChange={handleInputChange}
                                        >
                                            <option value="any">Minstens één van de geselecteerde producten gekocht</option>
                                            <option value="all">Alle geselecteerde producten gekocht</option>
                                        </Form.Select>
                                    </Form.Group>
                                )}

                                {/* Date range filters */}
                                <Form.Group className="mb-3">
                                    <Form.Label>Aankoopdatum (optioneel)</Form.Label>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <Form.Group className="mb-3">
                                                <Form.Label className="d-flex align-items-center gap-1">
                                                    Van
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip>
                                                                Dit is inclusief de gekozen datum.
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '16px',
                                                                height: '16px',
                                                                borderRadius: '50%',
                                                                backgroundColor: 'var(--ukies-dark-orange)',
                                                                color: 'white',
                                                                fontSize: '10px',
                                                                fontWeight: 'bold',
                                                                cursor: 'help',
                                                                marginTop: '-5px'
                                                            }}
                                                        >
                                                            i
                                                        </span>
                                                    </OverlayTrigger>
                                                </Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="purchasedAfter"
                                                    value={formData.purchasedAfter || ''}
                                                    onChange={handleInputChange}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group className="mb-3">
                                                <Form.Label className="d-flex align-items-center gap-1">
                                                    Tot en met
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip>
                                                                Dit is inclusief de gekozen datum.
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '16px',
                                                                height: '16px',
                                                                borderRadius: '50%',
                                                                backgroundColor: 'var(--ukies-dark-orange)',
                                                                color: 'white',
                                                                fontSize: '10px',
                                                                fontWeight: 'bold',
                                                                cursor: 'help',
                                                                marginTop: '-5px'
                                                            }}
                                                        >
                                                            i
                                                        </span>
                                                    </OverlayTrigger>
                                                </Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="purchasedBefore"
                                                    value={formData.purchasedBefore || ''}
                                                    onChange={handleInputChange}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </Form.Group>

                                {/* Only last purchased product checkbox */}
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        id="onlyLastProduct"
                                        name="onlyLastProduct"
                                        checked={formData.onlyLastProduct !== undefined ? formData.onlyLastProduct : true}
                                        onChange={handleInputChange}
                                        className="large-checkbox-container"
                                        label={
                                            <span className="d-flex align-items-center gap-1">
                                                Alleen het laatst gekochte product tonen
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            Als je voor deze optie kiest wordt de selectie ontdubbeld op klantnummer. Alleen de laatste aankoop wordt opgenomen in de selectie.
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '16px',
                                                            height: '16px',
                                                            borderRadius: '50%',
                                                            backgroundColor: 'var(--ukies-dark-orange)',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            fontWeight: 'bold',
                                                            cursor: 'help',
                                                            marginTop: '-5px'
                                                        }}
                                                    >
                                                        i
                                                    </span>
                                                </OverlayTrigger>
                                            </span>
                                        }
                                    />
                                </Form.Group>

                                {/* Newsletter optin requirement */}
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        id="optinRequired"
                                        name="optinRequired"
                                        checked={formData.optinRequired || false}
                                        onChange={handleInputChange}
                                        className="large-checkbox-container"
                                        label={
                                            <span className="d-flex align-items-center gap-1">
                                                Alleen klanten die ingeschreven zijn voor de nieuwsbrief
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            Als je voor deze optie kiest worden alleen klanten waarvan het emailadres is ingeschreven voor de nieuwsbrief opgenomen in de selectie.
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '16px',
                                                            height: '16px',
                                                            borderRadius: '50%',
                                                            backgroundColor: 'var(--ukies-dark-orange)',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            fontWeight: 'bold',
                                                            cursor: 'help',
                                                            marginTop: '-5px'
                                                        }}
                                                    >
                                                        i
                                                    </span>
                                                </OverlayTrigger>
                                            </span>
                                        }
                                    />
                                </Form.Group>
                            </div>
                        )}

                        {formData.type === 'product_category' && (
                            <div className="mt-3">
                                <Form.Group className="mb-3">
                                    <Form.Label>Categorie ID</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="categoryId"
                                        value={formData.categoryId || ''}
                                        onChange={handleInputChange}
                                        placeholder="Voer de categorie ID in..."
                                    />
                                    <Form.Text className="text-muted">
                                        Voer het ID in van de productcategorie
                                    </Form.Text>
                                </Form.Group>

                                {/* Only last purchased product checkbox */}
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        id="onlyLastProductCategory"
                                        name="onlyLastProduct"
                                        checked={formData.onlyLastProduct !== undefined ? formData.onlyLastProduct : true}
                                        onChange={handleInputChange}
                                        className="large-checkbox-container"
                                        label={
                                            <span className="d-flex align-items-center gap-1">
                                                Alleen het laatst gekochte product tonen
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            Als je voor deze optie kiest wordt de selectie ontdubbeld op klantnummer. Alleen de laatste aankoop wordt opgenomen in de selectie.
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '16px',
                                                            height: '16px',
                                                            borderRadius: '50%',
                                                            backgroundColor: 'var(--ukies-dark-orange)',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            fontWeight: 'bold',
                                                            cursor: 'help',
                                                            marginTop: '-5px'
                                                        }}
                                                    >
                                                        i
                                                    </span>
                                                </OverlayTrigger>
                                            </span>
                                        }
                                    />
                                </Form.Group>

                                {/* Newsletter optin requirement */}
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        id="optinRequiredCategory"
                                        name="optinRequired"
                                        checked={formData.optinRequired || false}
                                        onChange={handleInputChange}
                                        className="large-checkbox-container"
                                        label={
                                            <span className="d-flex align-items-center gap-1">
                                                Alleen klanten die ingeschreven zijn voor de nieuwsbrief
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            Als je voor deze optie kiest worden alleen klanten waarvan het emailadres is ingeschreven voor de nieuwsbrief opgenomen in de selectie.
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '16px',
                                                            height: '16px',
                                                            borderRadius: '50%',
                                                            backgroundColor: 'var(--ukies-dark-orange)',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            fontWeight: 'bold',
                                                            cursor: 'help',
                                                            marginTop: '-5px'
                                                        }}
                                                    >
                                                        i
                                                    </span>
                                                </OverlayTrigger>
                                            </span>
                                        }
                                    />
                                </Form.Group>
                            </div>
                        )}

                        {formData.type === 'form_entries' && (
                            <div className="mt-3">
                                <Form.Group className="mb-3">
                                    <Form.Label>Formulier <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        value={formData.formId || ''}
                                        onChange={handleFormEntriesFormSelect}
                                        disabled={forms.length === 0}
                                    >
                                        <option value="">Selecteer formulier...</option>
                                        {forms
                                            .filter(form => {
                                                const formName = form.name || form.title || `Form ${form.id}`;
                                                return !formName.toLowerCase().includes('nieuwsbrief inschrijvingen');
                                            })
                                            .sort((a, b) => {
                                                const nameA = (a.name || a.title || `Form ${a.id}`).toLowerCase();
                                                const nameB = (b.name || b.title || `Form ${b.id}`).toLowerCase();
                                                return nameA.localeCompare(nameB);
                                            })
                                            .map(form => (
                                                <option key={form.id} value={form.id.toString()}>
                                                    {form.name || form.title || `Form ${form.id}`}
                                                </option>
                                            ))
                                        }
                                    </Form.Select>
                                    {forms.length === 0 && (
                                        <Form.Text className="text-muted">Formulieren laden...</Form.Text>
                                    )}
                                </Form.Group>
                            </div>
                        )}

                        {/* Field selection - always shown for all selection types */}
                        {formData.type && (
                            <div className="mt-4">
                                <Form.Group className="mb-3">
                                <Form.Label>Velden in export <span className="text-danger">*</span></Form.Label>
                                <Dropdown 
                                    onSelect={(e) => e && e.preventDefault()}
                                    autoClose={false}
                                    show={showFieldsDropdown}
                                    onToggle={setShowFieldsDropdown}
                                >
                                    <Dropdown.Toggle
                                        variant="outline-secondary"
                                        id="fields-dropdown"
                                            className="w-100 text-start d-flex"
                                            disabled={formData.type === 'form_entries' && (!formData.formId || formFieldsLoading || formFields.length === 0)}
                                        >
                                            <span className="flex-grow-1">
                                                {formData.type === 'form_entries' && formFieldsLoading
                                                    ? 'Velden laden...'
                                                    : formData.fieldsEnabled && formData.fieldsEnabled.length > 0
                                                        ? `${formData.fieldsEnabled.length} veld(en) geselecteerd`
                                                        : 'Selecteer veld(en)...'
                                                }
                                            </span>
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu className="w-100" style={{ maxHeight: '340px', overflowY: 'scroll' }}>
                                            {(() => {
                                                if (formData.type === 'form_entries') {
                                                    const dynamicFieldOptions = formFields.map(f => ({
                                                        id: f.name,
                                                        label: f.label || prettifyFieldName(f.name)
                                                    }));

                                                    if (dynamicFieldOptions.length === 0) {
                                                        return <Dropdown.Item disabled>Geen velden beschikbaar voor dit formulier</Dropdown.Item>;
                                                    }

                                                    return dynamicFieldOptions.map((field) => {
                                                        const isSelected = formData.fieldsEnabled?.includes(field.id) || false;

                                                        return (
                                                            <Dropdown.Item
                                                                key={field.id}
                                                                as="div"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handleFieldToggle(field.id, !isSelected);
                                                                }}
                                                                className="d-flex align-items-center"
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        width: '20px',
                                                                        textAlign: 'center',
                                                                        color: isSelected ? '#28a745' : 'transparent',
                                                                        fontSize: '1.1rem',
                                                                        marginRight: '8px'
                                                                    }}
                                                                >
                                                                    ✓
                                                                </span>
                                                                <span className="flex-grow-1">{field.label}</span>
                                                            </Dropdown.Item>
                                                        );
                                                    });
                                                }

                                                let fieldOptions = [
                                                    { id: 'id', label: 'Klantnummer' },
                                                    { id: 'firstname', label: 'Voornaam' },
                                                    { id: 'lastname', label: 'Achternaam' },
                                                    { id: 'email', label: 'E-mail' },
                                                    { id: 'phone', label: 'Telefoonnummer' },
                                                    { id: 'city', label: 'Woonplaats' },
                                                    { id: 'zipcode', label: 'Postcode' },
                                                    { id: 'country', label: 'Land' }
                                                ];

                                                // Add purchasedProduct and purchaseDate fields only for specific_products and product_category types
                                                if (formData.type === 'specific_products' || formData.type === 'product_category') {
                                                    fieldOptions.push(
                                                        { id: 'purchasedProduct', label: 'Gekochte product' },
                                                        { id: 'purchaseDate', label: 'Aankoop datum' }
                                                    );
                                                }

                                                // Add optin fields for all types
                                                fieldOptions.push(
                                                    { id: 'optin', label: 'Optin status' },
                                                    { id: 'optinDate', label: 'Optin datum' },
                                                    { id: 'optinSource', label: 'Optin bron' }
                                                );

                                                return fieldOptions.map((field) => {
                                                    const isSelected = formData.fieldsEnabled?.includes(field.id) || false;
                                                    const isOptinRelatedDisabled = (field.id === 'optinSource' || field.id === 'optinDate') && !formData.fieldsEnabled?.includes('optin');
                                                    const isPurchaseDateDisabled = field.id === 'purchaseDate' && !formData.fieldsEnabled?.includes('purchasedProduct');
                                                    const isDisabled = isOptinRelatedDisabled || isPurchaseDateDisabled;
                                                    
                                                    return (
                                                        <Dropdown.Item 
                                                            key={field.id} 
                                                            as="div" 
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                if (!isDisabled) {
                                                                    handleFieldToggle(field.id, !isSelected);
                                                                }
                                                            }}
                                                            className="d-flex align-items-center"
                                                            style={{ 
                                                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                                opacity: isDisabled ? 0.5 : 1
                                                            }}
                                                            disabled={isDisabled}
                                                        >
                                                            <span 
                                                                style={{ 
                                                                    width: '20px', 
                                                                    textAlign: 'center', 
                                                                    color: isSelected ? '#28a745' : 'transparent',
                                                                    fontSize: '1.1rem',
                                                                    marginRight: '8px'
                                                                }}
                                                            >
                                                                ✓
                                                            </span>
                                                            <span className="flex-grow-1">{field.label}</span>
                                                        </Dropdown.Item>
                                                    );
                                                });
                                            })()}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    
                                    {/* Display selected fields */}
                                    {formData.fieldsEnabled && formData.fieldsEnabled.length > 0 && (
                                        <div className="mt-3">
                                            <Form.Label>Geselecteerde velden:</Form.Label>
                                            <div className="d-flex flex-wrap gap-2">
                                                {(() => {
                                                    // Define field order (same as dropdown)
                                                    let fieldOptions;
                                                    if (formData.type === 'form_entries') {
                                                        fieldOptions = formFields.map(f => ({
                                                            id: f.name,
                                                            label: f.label || prettifyFieldName(f.name)
                                                        }));
                                                    } else {
                                                        fieldOptions = [
                                                            { id: 'id', label: 'Klantnummer' },
                                                            { id: 'firstname', label: 'Voornaam' },
                                                            { id: 'lastname', label: 'Achternaam' },
                                                            { id: 'email', label: 'E-mail' },
                                                            { id: 'phone', label: 'Telefoonnummer' },
                                                            { id: 'city', label: 'Woonplaats' },
                                                            { id: 'zipcode', label: 'Postcode' },
                                                            { id: 'country', label: 'Land' }
                                                        ];

                                                        // Add purchasedProduct and purchaseDate fields only for specific_products and product_category types
                                                        if (formData.type === 'specific_products' || formData.type === 'product_category') {
                                                            fieldOptions.push(
                                                                { id: 'purchasedProduct', label: 'Gekochte product' },
                                                                { id: 'purchaseDate', label: 'Aankoop datum' }
                                                            );
                                                        }

                                                        // Add optin fields for all types
                                                        fieldOptions.push(
                                                            { id: 'optin', label: 'Optin status' },
                                                            { id: 'optinDate', label: 'Optin datum' },
                                                            { id: 'optinSource', label: 'Optin bron' }
                                                        );
                                                    }

                                                    // Create ordered list based on dropdown order
                                                    const orderedFields = fieldOptions.filter(field =>
                                                        formData.fieldsEnabled.includes(field.id)
                                                    );

                                                    return orderedFields.map((field) => {
                                                        return (
                                                            <span 
                                                                key={field.id} 
                                                                className="badge d-flex align-items-center"
                                                                style={{ 
                                                                    fontSize: '0.8rem',
                                                                    backgroundColor: 'var(--ukies-light-green)',
                                                                    color: 'white',
                                                                    padding: '0.5rem 0.75rem',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                <span style={{ marginRight: '0.5rem' }}>
                                                                    {field.label}
                                                                </span>
                                                                <span 
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        handleFieldToggle(field.id, false);
                                                                    }}
                                                                    style={{ 
                                                                        cursor: 'pointer',
                                                                        fontSize: '1rem',
                                                                        fontWeight: 'bold',
                                                                        opacity: 0.8,
                                                                        marginLeft: 'auto',
                                                                        marginTop: '-2px'
                                                                    }}
                                                                    title="Verwijderen"
                                                                >
                                                                    ×
                                                                </span>
                                                            </span>
                                                        );
                                                    });
                                                })()}
                                            </div>
                                        </div>
                                    )}

                                    {/* Warning for newsletter type with conflicting fields */}
                                    {formData.type === 'newsletter' && formData.fieldsEnabled && (() => {
                                        const conflictingFields = formData.fieldsEnabled.filter(fieldId => 
                                            ['id', 'firstname', 'lastname', 'phone', 'city', 'zipcode', 'country'].includes(fieldId)
                                        );
                                        const fieldLabels = {
                                            'id': 'Klantnummer',
                                            'firstname': 'Voornaam',
                                            'lastname': 'Achternaam',
                                            'phone': 'Telefoonnummer',
                                            'city': 'Woonplaats',
                                            'zipcode': 'Postcode',
                                            'country': 'Land'
                                        };
                                        
                                        if (conflictingFields.length > 0) {
                                            return (
                                                <div className="mt-3 p-3 bg-warning bg-opacity-10 border border-warning rounded">
                                                    <div className="d-flex align-items-start">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-exclamation-triangle text-warning me-2 mt-1 flex-shrink-0" viewBox="0 0 16 16">
                                                            <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.146.146 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.163.163 0 0 1-.054.06.116.116 0 0 1-.066.017H1.146a.115.115 0 0 1-.066-.017.163.163 0 0 1-.054-.06.176.176 0 0 1 .002-.183L7.884 2.073a.147.147 0 0 1 .054-.057zm1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566z"/>
                                                            <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995z"/>
                                                        </svg>
                                                        <div>
                                                            <small className="text-muted">
                                                                <strong>Let op:</strong> Nieuwsbrief inschrijvingen kunnen ook door niet-klanten gedaan worden, de volgende velden kunnen leeg zijn:<br />{conflictingFields.map(fieldId => fieldLabels[fieldId]).join(', ')}
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}

                                    {/* Warning for optinSource field when onlyLastOptin is disabled */}
                                    {formData.fieldsEnabled && (() => {
                                        const optinSourceSelected = formData.fieldsEnabled.includes('optinSource');
                                        const onlyLastOptinChecked = formData.onlyLastOptin || false;
                                        
                                        if (optinSourceSelected && onlyLastOptinChecked) {
                                            const fieldText = 'Optin bron';
                                            
                                            return (
                                                <div className="mt-3 p-3 bg-warning bg-opacity-10 border border-warning rounded">
                                                    <div className="d-flex align-items-start">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-exclamation-triangle text-warning me-2 mt-1 flex-shrink-0" viewBox="0 0 16 16">
                                                            <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.146.146 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.163.163 0 0 1-.054.06.116.116 0 0 1-.066.017H1.146a.115.115 0 0 1-.066-.017.163.163 0 0 1-.054-.06.176.176 0 0 1 .002-.183L7.884 2.073a.147.147 0 0 1 .054-.057zm1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566z"/>
                                                            <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995z"/>
                                                        </svg>
                                                        <div>
                                                            <small className="text-muted">
                                                                <strong>Let op:</strong> Je hebt gekozen voor "Alleen de laatste inschrijving tonen" in combinatie met het veld "{fieldText}". Hierdoor zal de selectie de laatste inschrijving per formulier tonen. Hetzelfde emailadres kan dus meerdere keren voorkomen.
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </Form.Group>
                            </div>
                        )}

                        {/* Warning for optin-related selections in product types */}
                        {(formData.type === 'specific_products' || formData.type === 'product_category') && 
                         (formData.fieldsEnabled?.includes('optin') || formData.optinRequired) && (
                            <div className="mt-3 p-3 bg-warning bg-opacity-10 border border-warning rounded">
                                <div className="d-flex align-items-start">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-exclamation-triangle text-warning me-2 mt-1 flex-shrink-0" viewBox="0 0 16 16">
                                        <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.146.146 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.163.163 0 0 1-.054.06.116.116 0 0 1-.066.017H1.146a.115.115 0 0 1-.066-.017.163.163 0 0 1-.054-.06.176.176 0 0 1 .002-.183L7.884 2.073a.147.147 0 0 1 .054-.057zm1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566z"/>
                                        <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995z"/>
                                    </svg>
                                    <div className="flex-grow-1">
                                        <small>
                                            Je hebt gekozen voor {(() => {
                                                const selectedOptions = [];
                                                if (formData.fieldsEnabled?.includes('optin')) {
                                                    selectedOptions.push('Optin status');
                                                }
                                                if (formData.optinRequired) {
                                                    selectedOptions.push('Alleen klanten die ingeschreven zijn voor de nieuwsbrief');
                                                }
                                                return selectedOptions.join(' en ');
                                            })()}. Dit betekent dat de klanten op email worden gematched aan nieuwsbriefinschrijvingen.
                                        </small>
                                    </div>
                                </div>
                            </div>
                        )}

                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={csvGenerating}>
                        Annuleren
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSave} 
                        disabled={saving || csvGenerating}
                        style={{
                            backgroundColor: '#3b4f46',
                            borderColor: '#3b4f46'
                        }}
                    >
                        {csvGenerating ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                CSV genereren...
                            </>
                        ) : saving ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Opslaan...
                            </>
                        ) : (
                            'Selectie aanmaken'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* View/Edit Selection Modal */}
            <Modal show={showViewModal} onHide={handleViewClose} size="lg" backdrop={true} keyboard={true}>
                <Modal.Header closeButton>
                    <Modal.Title>Selectie bekijken</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Naam van de selectie <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={viewFormData.name || ''}
                                onChange={handleViewInputChange}
                                placeholder="Voer een naam in voor deze selectie..."
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Beschrijving (optioneel)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="description"
                                value={viewFormData.description || ''}
                                onChange={handleViewInputChange}
                                placeholder="Optionele beschrijving van de selectie..."
                            />
                        </Form.Group>

                        <Row>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Aangemaakt door</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={selectedSelection?.created_by || ''}
                                        disabled
                                        style={{backgroundColor: '#f8f9fa'}}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Aangemaakt op</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formatDate(selectedSelection?.created_at)}
                                        disabled
                                        style={{backgroundColor: '#f8f9fa'}}
                                    />
                                </Form.Group>
                            </div>
                        </Row>

                        <hr />

                        <Form.Group className="mb-3">
                            <Form.Label>Type selectie <span className="text-danger">*</span></Form.Label>
                            <Form.Select
                                name="type"
                                value={viewFormData.type || ''}
                                disabled
                                style={{backgroundColor: '#f8f9fa'}}
                            >
                                <option value="">{viewFormData.type ? translateSelectionType(viewFormData.type) : 'Selecteer type selectie...'}</option>
                                <option value="newsletter">Nieuwsbrief inschrijvingen</option>
                                <option value="specific_products">Product aankopen</option>
                                <option value="product_category">Categorie aankopen</option>
                                <option value="form_entries">Formulier inzendingen</option>
                            </Form.Select>
                        </Form.Group>

                        {/* Conditional form fields based on selection type - all disabled */}
                        {viewFormData.type === 'newsletter' && (
                            <div className="mt-3">
                                <Form.Group className="mb-3">
                                    {/* Display selected forms as badges */}
                                    {selectedSelection?.selectionParameters?.forms && selectedSelection.selectionParameters.forms.length > 0 && (
                                        <div className="mt-3">
                                            <Form.Label>Geselecteerde formulieren:</Form.Label>
                                            <div className="d-flex flex-wrap gap-2">
                                                {(() => {
                                                    // Get newsletter forms in dropdown order (same logic as dropdown)
                                                    const newsletterForms = forms.filter(form => {
                                                        const formName = form.name || form.title || `Form ${form.id}`;
                                                        return formName.toLowerCase().includes('nieuwsbrief inschrijvingen');
                                                    });
                                                    
                                                    // Create ordered forms array maintaining dropdown order
                                                    const orderedForms = [];
                                                    
                                                    // Add newsletter forms first (in their dropdown order)
                                                    newsletterForms.forEach(form => {
                                                        if (selectedSelection.selectionParameters.forms.includes(form.id.toString())) {
                                                            orderedForms.push({
                                                                id: form.id.toString(),
                                                                name: form.name || form.title || `Form ${form.id}`
                                                            });
                                                        }
                                                    });
                                                    
                                                    // Add newsletter special case if present
                                                    if (selectedSelection.selectionParameters.forms.includes('newsletter')) {
                                                        orderedForms.push({
                                                            id: 'newsletter',
                                                            name: 'Nieuwsbrief'
                                                        });
                                                    }
                                                    
                                                    // Add any remaining forms (safety fallback)
                                                    selectedSelection.selectionParameters.forms.forEach(formId => {
                                                        if (!orderedForms.find(f => f.id === formId)) {
                                                            const formItem = forms.find(f => f.id.toString() === formId.toString());
                                                            orderedForms.push({
                                                                id: formId,
                                                                name: formItem ? (formItem.name || formItem.title || `Form ${formId}`) : `Unknown Form (${formId})`
                                                            });
                                                        }
                                                    });
                                                    
                                                    return orderedForms.map((form) => (
                                                        <span 
                                                            key={form.id} 
                                                            className="badge"
                                                            style={{ 
                                                                fontSize: '0.8rem',
                                                                backgroundColor: 'var(--ukies-light-green)',
                                                                color: 'white',
                                                                padding: '0.5rem 0.75rem'
                                                            }}
                                                        >
                                                            {form.name}
                                                        </span>
                                                    ));
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </Form.Group>

                                {/* Selection mode - only show if multiple forms selected */}
                                {selectedSelection?.selectionParameters?.forms && selectedSelection.selectionParameters.forms.length > 1 && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Selectie modus</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={selectedSelection?.selectionParameters?.operator === 'all' 
                                                ? 'Ingeschreven op alle formulieren' 
                                                : 'Ingeschreven op minstens één van de formulieren'}
                                            disabled
                                            style={{backgroundColor: '#f8f9fa'}}
                                        />
                                    </Form.Group>
                                )}
                                
                                {/* Only last optin checkbox */}
                                <div className="mt-3">
                                    <Form.Check
                                        type="checkbox"
                                        id="viewOnlyLastOptin"
                                        name="onlyLastOptin"
                                        checked={viewFormData.onlyLastOptin || false}
                                        disabled
                                        className="large-checkbox-container"
                                        label={
                                            <span className="d-flex align-items-center gap-1">
                                                Alleen de laatste inschrijving tonen
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            Als je voor deze optie kiest wordt de selectie ontdubbeld op emailadres. Alleen de laatste inschrijving per emailadres wordt opgenomen in de selectie.
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '16px',
                                                            height: '16px',
                                                            borderRadius: '50%',
                                                            backgroundColor: 'var(--ukies-dark-orange)',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            fontWeight: 'bold',
                                                            cursor: 'help',
                                                            marginTop: '-5px'
                                                        }}
                                                    >
                                                        i
                                                    </span>
                                                </OverlayTrigger>
                                            </span>
                                        }
                                    />
                                </div>

                                {/* Customers only checkbox */}
                                <div className="mt-3">
                                    <Form.Check
                                        type="checkbox"
                                        id="viewCustomersOnly"
                                        name="customersOnly"
                                        checked={viewFormData.customersOnly || false}
                                        disabled
                                        className="large-checkbox-container"
                                        label={
                                            <span className="d-flex align-items-center gap-1">
                                                Alleen klanten in de selectie
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            Als je voor deze optie kiest worden alleen klanten opgenomen in de selectie. Alle inschrijvingen waarvan het emailadres niet gematched kan worden aan een klant worden uit de selectie verwijderd.
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '16px',
                                                            height: '16px',
                                                            borderRadius: '50%',
                                                            backgroundColor: 'var(--ukies-dark-orange)',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            fontWeight: 'bold',
                                                            cursor: 'help',
                                                            marginTop: '-5px'
                                                        }}
                                                    >
                                                        i
                                                    </span>
                                                </OverlayTrigger>
                                            </span>
                                        }
                                    />
                                </div>
                            </div>
                        )}

                        {viewFormData.type === 'specific_products' && (
                            <div className="mt-3">
                                {/* Display selected products as badges */}
                                {selectedSelection?.selectionParameters?.products && selectedSelection.selectionParameters.products.length > 0 && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Geselecteerde producten:</Form.Label>
                                        <div className="d-flex flex-wrap gap-2">
                                            {(() => {
                                                // Get product details for selected products
                                                const selectedProducts = products
                                                    .filter(product => selectedSelection.selectionParameters.products.includes(product.id.toString()))
                                                    .sort((a, b) => {
                                                        const titleA = (a.title || `Product ${a.id}`).toLowerCase();
                                                        const titleB = (b.title || `Product ${b.id}`).toLowerCase();
                                                        return titleA.localeCompare(titleB);
                                                    });
                                                
                                                return selectedProducts.map((product) => (
                                                    <span 
                                                        key={product.id} 
                                                        className="badge"
                                                        style={{ 
                                                            fontSize: '0.8rem',
                                                            backgroundColor: 'var(--ukies-light-green)',
                                                            color: 'white',
                                                            padding: '0.5rem 0.75rem'
                                                        }}
                                                    >
                                                        {product.title || `Product ${product.id}`}
                                                    </span>
                                                ));
                                            })()}
                                        </div>
                                    </Form.Group>
                                )}

                                {/* Product selection mode - only show if multiple products selected */}
                                {selectedSelection?.selectionParameters?.products && selectedSelection.selectionParameters.products.length > 1 && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Selectie modus</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={selectedSelection?.selectionParameters?.productOperator === 'all' 
                                                ? 'Alle geselecteerde producten gekocht' 
                                                : 'Minstens één van de geselecteerde producten gekocht'}
                                            disabled
                                            style={{backgroundColor: '#f8f9fa'}}
                                        />
                                    </Form.Group>
                                )}

                                {/* Date range filters - only show if dates were set */}
                                {(selectedSelection?.selectionParameters?.purchasedAfter || selectedSelection?.selectionParameters?.purchasedBefore) && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Aankoopdatum (optioneel)</Form.Label>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="d-flex align-items-center gap-1">
                                                        Van
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                                <Tooltip>
                                                                    Dit is inclusief de gekozen datum.
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <span
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: '16px',
                                                                    height: '16px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: 'var(--ukies-dark-orange)',
                                                                    color: 'white',
                                                                    fontSize: '10px',
                                                                    fontWeight: 'bold',
                                                                    cursor: 'help',
                                                                    marginTop: '-5px'
                                                                }}
                                                            >
                                                                i
                                                            </span>
                                                        </OverlayTrigger>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type={selectedSelection?.selectionParameters?.purchasedAfter ? 'date' : 'text'}
                                                        value={selectedSelection?.selectionParameters?.purchasedAfter || ''}
                                                        disabled
                                                        style={{backgroundColor: '#f8f9fa'}}
                                                    />
                                                </Form.Group>
                                            </div>
                                            <div className="col-md-6">
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="d-flex align-items-center gap-1">
                                                        Tot en met
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                                <Tooltip>
                                                                    Dit is inclusief de gekozen datum.
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <span
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: '16px',
                                                                    height: '16px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: 'var(--ukies-dark-orange)',
                                                                    color: 'white',
                                                                    fontSize: '10px',
                                                                    fontWeight: 'bold',
                                                                    cursor: 'help',
                                                                    marginTop: '-5px'
                                                                }}
                                                            >
                                                                i
                                                            </span>
                                                        </OverlayTrigger>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type={selectedSelection?.selectionParameters?.purchasedBefore ? 'date' : 'text'}
                                                        value={selectedSelection?.selectionParameters?.purchasedBefore || ''}
                                                        disabled
                                                        style={{backgroundColor: '#f8f9fa'}}
                                                    />
                                                </Form.Group>
                                            </div>
                                        </div>
                                    </Form.Group>
                                )}

                                {/* Only last purchased product checkbox */}
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        id="viewOnlyLastProduct"
                                        checked={selectedSelection?.selectionParameters?.onlyLastProduct !== false}
                                        disabled
                                        className="large-checkbox-container"
                                        label={
                                            <span className="d-flex align-items-center gap-1">
                                                Alleen het laatst gekochte product tonen
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            Als je voor deze optie kiest wordt de selectie ontdubbeld op klantnummer. Alleen de laatste aankoop wordt opgenomen in de selectie.
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '16px',
                                                            height: '16px',
                                                            borderRadius: '50%',
                                                            backgroundColor: 'var(--ukies-dark-orange)',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            fontWeight: 'bold',
                                                            cursor: 'help',
                                                            marginTop: '-5px'
                                                        }}
                                                    >
                                                        i
                                                    </span>
                                                </OverlayTrigger>
                                            </span>
                                        }
                                    />
                                </Form.Group>

                                {/* Newsletter optin requirement */}
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        id="viewOptinRequired"
                                        checked={selectedSelection?.selectionParameters?.optinRequired || false}
                                        disabled
                                        className="large-checkbox-container"
                                        label={
                                            <span className="d-flex align-items-center gap-1">
                                                Alleen klanten die ingeschreven zijn voor de nieuwsbrief
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            Als je voor deze optie kiest worden alleen klanten waarvan het emailadres is ingeschreven voor de nieuwsbrief opgenomen in de selectie.
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '16px',
                                                            height: '16px',
                                                            borderRadius: '50%',
                                                            backgroundColor: 'var(--ukies-dark-orange)',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            fontWeight: 'bold',
                                                            cursor: 'help',
                                                            marginTop: '-5px'
                                                        }}
                                                    >
                                                        i
                                                    </span>
                                                </OverlayTrigger>
                                            </span>
                                        }
                                    />
                                </Form.Group>
                            </div>
                        )}

                        {viewFormData.type === 'product_category' && (
                            <div className="mt-3">
                                <Form.Group className="mb-3">
                                    <Form.Label>Categorie ID</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={selectedSelection?.selectionParameters?.categories?.[0] || ''}
                                        disabled
                                        style={{backgroundColor: '#f8f9fa'}}
                                    />
                                    <Form.Text className="text-muted">
                                        ID van de productcategorie
                                    </Form.Text>
                                </Form.Group>
                            </div>
                        )}

                        {viewFormData.type === 'form_entries' && (
                            <div className="mt-3">
                                <Form.Group className="mb-3">
                                    <Form.Label>Formulier</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={(() => {
                                            const formId = selectedSelection?.selectionParameters?.formId;
                                            if (!formId) return '';
                                            const form = forms.find(f => f.id.toString() === formId.toString());
                                            return form ? (form.name || form.title || `Form ${formId}`) : `Form ${formId}`;
                                        })()}
                                        disabled
                                        style={{backgroundColor: '#f8f9fa'}}
                                    />
                                </Form.Group>
                            </div>
                        )}

                        {/* Field selection - always shown for all selection types */}
                        {viewFormData.type && (
                            <div className="mt-4">
                                <Form.Group className="mb-3">
                                    {/* Display selected fields as badges */}
                                    {viewFormData.fieldsEnabled && viewFormData.fieldsEnabled.length > 0 && (
                                        <div className="mt-3">
                                            <Form.Label>Geselecteerde velden:</Form.Label>
                                            <div className="d-flex flex-wrap gap-2">
                                                {(() => {
                                                    if (viewFormData.type === 'form_entries') {
                                                        // Use the field labels snapshotted at creation time
                                                        const fieldLabels = selectedSelection?.selectionParameters?.fieldLabels || {};
                                                        return viewFormData.fieldsEnabled.map((fieldId) => (
                                                            <span
                                                                key={fieldId}
                                                                className="badge"
                                                                style={{
                                                                    fontSize: '0.8rem',
                                                                    backgroundColor: 'var(--ukies-light-green)',
                                                                    color: 'white',
                                                                    padding: '0.5rem 0.75rem'
                                                                }}
                                                            >
                                                                {fieldLabels[fieldId] || fieldId}
                                                            </span>
                                                        ));
                                                    }

                                                    // Define field order (same as dropdown)
                                                    const fieldOptions = [
                                                        { id: 'id', label: 'Klantnummer' },
                                                        { id: 'firstname', label: 'Voornaam' },
                                                        { id: 'lastname', label: 'Achternaam' },
                                                        { id: 'email', label: 'E-mail' },
                                                        { id: 'phone', label: 'Telefoonnummer' },
                                                        { id: 'city', label: 'Woonplaats' },
                                                        { id: 'zipcode', label: 'Postcode' },
                                                        { id: 'country', label: 'Land' },
                                                        { id: 'purchasedProduct', label: 'Gekochte product' },
                                                        { id: 'purchaseDate', label: 'Aankoop datum' },
                                                        { id: 'optin', label: 'Optin status' },
                                                        { id: 'optinDate', label: 'Optin datum' },
                                                        { id: 'optinSource', label: 'Optin bron' }
                                                    ];

                                                    // Create ordered list based on dropdown order
                                                    const orderedFields = fieldOptions.filter(field =>
                                                        viewFormData.fieldsEnabled.includes(field.id)
                                                    );

                                                    return orderedFields.map((field) => {
                                                        return (
                                                            <span
                                                                key={field.id}
                                                                className="badge"
                                                                style={{
                                                                    fontSize: '0.8rem',
                                                                    backgroundColor: 'var(--ukies-light-green)',
                                                                    color: 'white',
                                                                    padding: '0.5rem 0.75rem'
                                                                }}
                                                            >
                                                                {field.label}
                                                            </span>
                                                        );
                                                    });
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </Form.Group>
                            </div>
                        )}

                        <hr />

                        {/* Download Section - centered with record count */}
                        {selectedSelection?.fileLink && (
                            <div className="text-center mt-4">
                                <div className="mb-3">
                                    <h3 style={{ fontWeight: 'bold', color: '#3b4f46' }}>
                                        {formatNumber(selectedSelection?.result)} records
                                    </h3>
                                </div>
                                <Button 
                                    variant="success"
                                    onClick={handleDownload}
                                    disabled={downloading}
                                    size="lg"
                                    style={{
                                        backgroundColor: 'var(--ukies-light-green)',
                                        borderColor: 'var(--ukies-light-green)',
                                        color: 'white',
                                        whiteSpace: 'nowrap'
                                    }}
                                    title="CSV bestand downloaden"
                                >
                                    {downloading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Downloaden...
                                        </>
                                    ) : (
                                        <>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-download me-2" viewBox="0 0 16 16">
                                                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                                                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                                            </svg>
                                            CSV downloaden
                                        </>
                                    )}
                                </Button>
                                <div className="mt-2">
                                    <small className="text-muted" style={{fontStyle: 'italic'}}>
                                        Laatst gedownload: {formatDate(selectedSelection?.last_download) || 'Nooit'}
                                    </small>
                                </div>
                            </div>
                        )}

                    </Form>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-end align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <Form.Switch
                            id="delete-enable-switch"
                            label=""
                            checked={deleteEnabled}
                            onChange={(e) => setDeleteEnabled(e.target.checked)}
                            className="custom-switch-large"
                            title="Schakel in om verwijderen toe te staan"
                            style={{ 
                                fontSize: '0.7rem',
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '0'
                            }}
                        />
                        <Button 
                            variant="danger" 
                            onClick={handleDeleteSelection}
                            disabled={!deleteEnabled || deleting}
                            title="Selectie permanent verwijderen"
                        >
                            {deleting ? 'Verwijderen...' : 'Verwijderen'}
                        </Button>
                    </div>
                    <Button variant="secondary" onClick={handleViewClose}>
                        Annuleren
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSaveChanges} 
                        disabled={saving}
                        style={{
                            backgroundColor: '#3b4f46',
                            borderColor: '#3b4f46'
                        }}
                    >
                        {saving ? 'Opslaan...' : 'Opslaan'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
